// ================================================================
// SERVIÇO DE IA - CifrasBand
// Busca real no CifraClub via proxy → sem invenção de resultados
// Detecção de acordes via Spotify Basic Pitch (análise de áudio real)
// Transcrição de letra via Groq Whisper
// ================================================================

const GEMINI_KEY    = process.env.REACT_APP_GEMINI_KEY
const ANTHROPIC_KEY = process.env.REACT_APP_ANTHROPIC_KEY
const GROQ_KEY      = process.env.REACT_APP_GROQ_KEY

// ----------------------------------------------------------------
// GEMINI — apenas para imagens, PDF e fallbacks
// ----------------------------------------------------------------
async function chamarGemini(prompt, b64 = null, mime = null) {
  if (!GEMINI_KEY) throw new Error('REACT_APP_GEMINI_KEY não configurada')
  const parts = []
  if (b64 && mime) parts.push({ inline_data: { mime_type: mime, data: b64 } })
  parts.push({ text: prompt })
  for (const m of ['gemini-2.5-flash', 'gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-2.5-pro']) {
    try {
      const r = await fetch(
        `https://generativelanguage.googleapis.com/v1beta/models/${m}:generateContent?key=${GEMINI_KEY}`,
        { method:'POST', headers:{'Content-Type':'application/json'},
          body: JSON.stringify({ contents:[{role:'user',parts}], generationConfig:{temperature:0.1,maxOutputTokens:4096} }) }
      )
      if (!r.ok) continue
      const d = await r.json()
      const t = d?.candidates?.[0]?.content?.parts?.[0]?.text
      if (t) return t
    } catch {}
  }
  throw new Error('Gemini indisponível')
}

// ----------------------------------------------------------------
// GROQ — LLaMA 3.3 grátis
// ----------------------------------------------------------------
async function chamarGroq(prompt) {
  if (!GROQ_KEY) return null
  // Tenta modelos em cascata — cada um tem cota independente
  const MODELOS_JSON = ['llama-3.3-70b-versatile', 'llama-3.1-8b-instant', 'llama-3.2-3b-preview']
  for (const model of MODELOS_JSON) {
    try {
      const r = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${GROQ_KEY}` },
        body: JSON.stringify({
          model,
          messages: [
            { role: 'system', content: 'Você é especialista em cifras musicais brasileiras. Responda SOMENTE com JSON válido, sem markdown, sem texto extra.' },
            { role: 'user', content: prompt }
          ],
          temperature: 0.1,
          max_tokens: 4096
        })
      })
      if (r.status === 429) { console.warn(`[Groq] ${model} rate limit, tentando próximo`); continue }
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        console.warn(`[Groq] ${model} erro:`, e?.error?.message || r.status)
        continue
      }
      const d = await r.json()
      const content = d?.choices?.[0]?.message?.content
      if (content) return content
    } catch (e) {
      console.warn(`[Groq] ${model} falhou:`, e.message)
    }
  }
  return null
}

// Variante do Groq que devolve TEXTO LIVRE (não JSON) — para polir cifras
// Tenta modelos mais potentes primeiro
async function chamarGroqTexto(systemPrompt, userPrompt) {
  if (!GROQ_KEY) return null
  const MODELOS = [
    'llama-3.3-70b-versatile',   // principal — rápido e potente
    'llama-3.1-8b-instant'       // fallback caso o primeiro falhe
  ]
  for (const modelo of MODELOS) {
    try {
      const r = await fetch('https://api.groq.com/openai/v1/chat/completions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${GROQ_KEY}` },
        body: JSON.stringify({
          model: modelo,
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userPrompt }
          ],
          temperature: 0.2,
          max_tokens: 4096
        }),
        signal: AbortSignal.timeout(45000)   // 45s timeout — garante que não trava
      })
      if (!r.ok) {
        const e = await r.json().catch(() => ({}))
        console.warn(`[Groq ${modelo}] erro:`, e?.error?.message || r.status)
        continue
      }
      const d = await r.json()
      const t = d?.choices?.[0]?.message?.content
      if (t && t.length > 50) {
        console.log(`[Groq ${modelo}] respondeu ${t.length} chars`)
        return t
      }
    } catch (e) {
      console.warn(`[Groq ${modelo}] falhou:`, e.message)
    }
  }
  return null
}

// ----------------------------------------------------------------
// CLAUDE COM BUSCA WEB — fallback para busca
// ----------------------------------------------------------------
const CLAUDE_MODELS = ['claude-sonnet-4-6', 'claude-opus-4-5', 'claude-sonnet-4-5']

async function claudeComBuscaWeb(pergunta) {
  if (!ANTHROPIC_KEY) return null
  for (const model of CLAUDE_MODELS) {
    try {
      const res = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': ANTHROPIC_KEY,
          'anthropic-version': '2023-06-01',
          'anthropic-dangerous-direct-browser-calls': 'true'
        },
        body: JSON.stringify({
          model,
          max_tokens: 4096,
          tools: [{ type: 'web_search_20250305', name: 'web_search' }],
          messages: [{ role: 'user', content: pergunta }]
        })
      })
      if (!res.ok) continue
      const data = await res.json()
      const texto = (data.content || []).filter(b => b.type === 'text').map(b => b.text).join('\n')
      if (texto) return texto
    } catch (e) {
      console.warn(`[Claude web_search] erro:`, e.message)
    }
  }
  return null
}

// ----------------------------------------------------------------
// HELPER
// ----------------------------------------------------------------
function parseJSON(texto) {
  if (!texto) return null
  const limpo = texto.replace(/```json/gi,'').replace(/```/g,'').trim()
  const m = limpo.match(/\{[\s\S]*\}/)
  if (m) { try { return JSON.parse(m[0]) } catch {} }
  try { return JSON.parse(limpo) } catch {}
  return null
}

function slug(s='') {
  return s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'')
    .replace(/[^a-z0-9\s-]/g,'').replace(/\s+/g,'-').trim()
}

// ================================================================
// PROXY GRATUITO — múltiplos proxies com fallback
// NOTA: página de BUSCA do CifraClub é CSR (sem dados no HTML)
//       página de MÚSICA individual é SSR (cifra está no HTML) ✓
// ================================================================
// Proxies — nosso próprio proxy Vercel primeiro, depois públicos como fallback
const PROXIES = [
  // ★ Nosso proxy serverless (Vercel) — sem CORS, sem bloqueios, user-agent real
  { url: u => `/api/proxy?url=${encodeURIComponent(u)}`, json: false, proprio: true },
  // corsproxy.io — fallback público
  { url: u => `https://corsproxy.io/?${encodeURIComponent(u)}`,                          json: false },
  // codetabs — segunda opção
  { url: u => `https://api.codetabs.com/v1/proxy?quest=${encodeURIComponent(u)}`,        json: false },
  // allorigins (instável em 2026 — fallback)
  { url: u => `https://api.allorigins.win/raw?url=${encodeURIComponent(u)}`, json: false },
  { url: u => `https://api.allorigins.win/get?url=${encodeURIComponent(u)}`, json: true  },
]

// ----------------------------------------------------------------
// HELPER — converte notação [G]letra para acordes em linha separada
// Usada por sites que escrevem: "[G]Primeira [D]linha da letra"
// Resultado: "G         D\nPrimeira linha da letra"
// ----------------------------------------------------------------
function converterBracketsParaLinhas(texto) {
  return texto.split('\n').map(linha => {
    if (!/\[[A-G][^\]]{0,5}\]/.test(linha)) return linha
    let linhaAcordes = '', linhaLetra = '', i = 0
    while (i < linha.length) {
      if (linha[i] === '[') {
        const fim = linha.indexOf(']', i)
        if (fim !== -1) {
          const acorde = linha.slice(i + 1, fim)
          const col = linhaLetra.length
          while (linhaAcordes.length < col) linhaAcordes += ' '
          linhaAcordes += acorde
          i = fim + 1
          continue
        }
      }
      linhaLetra += linha[i++]
    }
    const partes = []
    if (linhaAcordes.trim()) partes.push(linhaAcordes)
    if (linhaLetra.trim()) partes.push(linhaLetra)
    return partes.join('\n')
  }).join('\n')
}

async function fetchViaProxy(url, timeout = 6000) {
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  const proxiesDisp = PROXIES.filter(p => !(p.proprio && isLocalDev))

  const tentarProxy = async (proxy) => {
    const proxyUrl = proxy.url(url)
    const r = await fetch(proxyUrl, { signal: AbortSignal.timeout(timeout) })
    if (!r.ok) throw new Error(`HTTP ${r.status}`)
    let html
    if (proxy.json) {
      const d = await r.json()
      html = d.contents || d.data || ''
    } else {
      html = await r.text()
    }
    if (!html || html.length <= 200) throw new Error('Resposta vazia')
    if (/<title>\s*(?:access denied|403|forbidden|blocked|erro de acesso)\s*<\/title>/i.test(html) ||
        /you don't have permission to access/i.test(html) ||
        /<h1>\s*(?:access denied|forbidden|blocked)\s*<\/h1>/i.test(html)) {
      throw new Error('Access Denied')
    }
    if (proxy.proprio) console.log('[proxy] ✓ proxy próprio funcionou')
    return html
  }

  // Tenta todos os proxies em paralelo — retorna o primeiro que funcionar
  try {
    return await Promise.any(proxiesDisp.map(tentarProxy))
  } catch {
    throw new Error('Todos os proxies falharam para: ' + url)
  }
}

// ----------------------------------------------------------------
// EXTRAI URLs de cifra de qualquer HTML de resultado de busca
// ----------------------------------------------------------------
const SLUGS_IGNORADOS = new Set([
  'busca','artistas','cifras','videos','noticias','login','cadastro','musicas',
  'curso','cursos','aulas','aprenda','aprender','fingerstyle','toque-tambem',
  'toque-também','parceiros','downloads','aplicativo','app','sobre','contato',
  'privacidade','termos','loja','blog','categorias','estilos','acordes',
  'escalas','teoria','upload','equipe','ajuda','faq','imprimir','favoritos',
  'playlist','tabs','tab','pro','premium','assinatura','planos','cifra-club'
])

function extrairUrlsCifraClub(html) {
  const padrao = /https?:\/\/(?:www\.)?cifraclub\.com\.br\/([a-z0-9][a-z0-9-]{0,48})\/([a-z0-9][a-z0-9-]{0,58})\//g
  const visto = new Set()
  const urls = []
  for (const m of [...html.matchAll(padrao)]) {
    const a = m[1], b = m[2]
    if (SLUGS_IGNORADOS.has(a) || SLUGS_IGNORADOS.has(b)) continue
    if (/^(curso|aula|toque|aprend|fingerstyle|teoria)/.test(a)) continue
    const url = `https://www.cifraclub.com.br/${a}/${b}/`
    if (!visto.has(url)) { visto.add(url); urls.push(url) }
    if (urls.length >= 6) break
  }
  return urls
}

function extrairUrlsCifrasCom(html) {
  const padrao = /https?:\/\/(?:www\.)?cifras\.com\.br\/([a-z0-9-]+)\/([a-z0-9-]+)/g
  const visto = new Set()
  const urls = []
  for (const m of [...html.matchAll(padrao)]) {
    const url = m[0].split('"')[0].split("'")[0]
    if (!visto.has(url)) { visto.add(url); urls.push(url) }
    if (urls.length >= 4) break
  }
  return urls
}

// Remove acentos para queries que podem falhar com caracteres especiais
function semAcentos(s) {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '')
}

// ----------------------------------------------------------------
// BUSCA MULTI-ENGINE — tenta várias engines em ordem até achar URLs
// 1. Google com site: + query original
// 2. Google com site: + query sem acentos
// 3. DuckDuckGo HTML (menos bot-detection, HTML estático)
// 4. Google sem site: + "cifra" → filtra URLs do CifraClub nos resultados
// ----------------------------------------------------------------
async function buscarUrlsMultiSite(query) {
  const qEnc       = encodeURIComponent(query)
  const qNorm      = encodeURIComponent(semAcentos(query))
  const qCifra     = encodeURIComponent(query + ' cifra')
  const qCifraNorm = encodeURIComponent(semAcentos(query) + ' cifra')

  const engines = [
    // 1. Google site: + "cifra" — "cifra" faz o Google corrigir typos (ex: "sigle→single")
    { url: `https://www.google.com/search?q=${qCifra}+site:cifraclub.com.br&hl=pt-BR&num=10&gl=BR`,
      cc: extrairUrlsCifraClub, cb: null, label: 'Google+site+cifra' },
    // 2. DuckDuckGo site: + "cifra" normalizado (sem acentos, sem bot-block)
    { url: `https://html.duckduckgo.com/html/?q=site%3Acifraclub.com.br+${qCifraNorm}&kl=br-pt`,
      cc: extrairUrlsCifraClub, cb: null, label: 'DDG+site+cifra' },
    // 3. Google geral + "cifra" — como o usuário busca manualmente no Google
    { url: `https://www.google.com/search?q=${qCifra}&hl=pt-BR&num=10&gl=BR`,
      cc: extrairUrlsCifraClub, cb: extrairUrlsCifrasCom, label: 'Google+cifra' },
    // 4. DuckDuckGo geral + "cifra violão"
    { url: `https://html.duckduckgo.com/html/?q=${encodeURIComponent(semAcentos(query) + ' cifra violao')}&kl=br-pt`,
      cc: extrairUrlsCifraClub, cb: extrairUrlsCifrasCom, label: 'DDG+cifra' },
  ]

  const visto = new Set()
  const dedup = urls => urls.filter(u => { if (visto.has(u)) return false; visto.add(u); return true })

  for (const eng of engines) {
    try {
      const html = await fetchViaProxy(eng.url, 12000)
      const cc = eng.cc ? eng.cc(html) : []
      const cb = eng.cb ? eng.cb(html) : []
      const encontrados = dedup([...cc, ...cb])
      console.log(`[${eng.label}] ${encontrados.length} URLs para "${query}"`)
      if (encontrados.length > 0) return encontrados
    } catch (e) {
      console.warn(`[${eng.label}]`, e.message)
    }
  }

  console.warn(`[busca] nenhuma engine retornou URLs para "${query}"`)
  return []
}

// ----------------------------------------------------------------
// IDENTIFICA SLUGS DO CIFRACLUB via Groq como fallback
// Usado quando Google não retornar resultados
// ----------------------------------------------------------------
async function buscarSlugsCifraClub(query) {
  const prompt = `Você conhece o site Cifra Club (cifraclub.com.br).
Pesquisa do usuário (pode conter erros de digitação!): "${query}"

IMPORTANTE: Corrija erros de digitação antes de identificar a música.
Exemplos de correção: "sigle core" → artista "Singlecore", "fernandno" → "Fernandinho", "gabrile rocha" → "Gabriela Rocha", "alin barros" → "Aline Barros".

Identifique as músicas no Cifra Club que correspondem à busca (já com typos corrigidos).
REGRAS de slug: sem acento, espaços → hífen, tudo minúsculo.
Exemplos de slugs: "Comunidade Shalom" → "comunidade-shalom", "Singlecore" → "singlecore", "Padre Fábio de Melo" → "pe-fabio-de-melo", "Gabriela Rocha" → "gabriela-rocha".

Inclua sua melhor estimativa mesmo sem certeza — preferível a deixar vazio.

JSON (sem markdown):
{"resultados":[
  {"artista_slug":"singlecore","musica_slug":"teu-fogo","artista_nome":"Singlecore","musica_nome":"Teu Fogo"}
]}`

  try {
    const r = await chamarGroq(prompt)
    const data = parseJSON(r)
    return (data?.resultados || []).map(s => `https://www.cifraclub.com.br/${s.artista_slug}/${s.musica_slug}/`)
  } catch { return [] }
}

// ----------------------------------------------------------------
// ----------------------------------------------------------------
// CACHE DE CIFRAS NO LOCALSTORAGE — evita regerar a mesma música
// TTL: 7 dias
// ----------------------------------------------------------------
function cifraCacheGet(titulo, artista) {
  try {
    const k = `cifra_v1_${slug(titulo)}_${slug(artista)}`
    const v = localStorage.getItem(k)
    if (!v) return null
    const { data, ts } = JSON.parse(v)
    if (Date.now() - ts > 7 * 24 * 60 * 60 * 1000) { localStorage.removeItem(k); return null }
    return data
  } catch { return null }
}

function cifraCacheSet(titulo, artista, data) {
  try {
    const k = `cifra_v1_${slug(titulo)}_${slug(artista)}`
    localStorage.setItem(k, JSON.stringify({ data, ts: Date.now() }))
    // Limpa entradas antigas se localStorage ficar cheio
  } catch {}
}

// ----------------------------------------------------------------
// GERA CIFRA COM GEMINI — gerador primário (cota maior que Groq)
// ----------------------------------------------------------------
async function gerarCifraComGemini(titulo, artista) {
  if (!GEMINI_KEY) return null
  const prompt = `Gere a cifra completa (letra + acordes) de "${titulo}" por ${artista}.
Use os acordes REAIS desta música. Se não conhecer, retorne cifra_completa: null.
Acordes em linha separada acima da letra. Inclua INTRO, VERSO, REFRÃO, PONTE.

Retorne SOMENTE JSON (sem markdown, sem explicações):
{"titulo":"${titulo}","artista":"${artista}","tom_original":"Lá (Am)","acordes":["Am","G","F","E"],"genero":"Gospel","cifra_completa":"INTRO\\n[acordes reais aqui]\\n\\nVERSO\\n[acorde]    [acorde]\\n[letra real da música]\\n[acorde]    [acorde]\\n[continuação da letra]"}`
  try {
    const r = await chamarGemini(prompt)
    const data = parseJSON(r)
    if (data?.cifra_completa?.length > 60 && data?.artista && temAcordes(data.cifra_completa)) {
      return {
        titulo: data.titulo || titulo, artista: data.artista || artista,
        tom_original: data.tom_original || null, acordes: data.acordes || [],
        genero: data.genero || '', cifra_completa: data.cifra_completa,
        fonte: 'gemini_gerado',
        link_cifraclub: `https://www.cifraclub.com.br/${slug(artista)}/${slug(titulo)}/`
      }
    }
    return null
  } catch { return null }
}

// Detecta se o output é o template do prompt copiado pelo modelo (rejeita)
function isTemplateCopied(s) {
  if (!s) return true
  return s.includes('Primeira linha da letra') ||
         s.includes('Segunda linha da letra') ||
         (s.includes('Primeira linha') && s.includes('Segunda linha')) ||
         s.includes('<LETRA_REAL>') ||
         s.includes('[letra real')
}

// ----------------------------------------------------------------
// GERA CIFRA POR ARTISTA — exportado para uso no clique do card lazy
// Tenta: cache → proxy URL real → Gemini → Groq
// ----------------------------------------------------------------
export async function gerarCifraPorArtista(titulo, artista, url = null) {
  // 1. Cache local
  const cached = cifraCacheGet(titulo, artista)
  if (cached?.cifra_completa && temAcordes(cached.cifra_completa) && !isTemplateCopied(cached.cifra_completa)) return cached

  // 2a. /api/cifra-extract (server-side) — usa API REST do CifraClub que não é bloqueada
  if (url) {
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    if (!isLocalDev) {
      try {
        console.log('[lazy] tentando /api/cifra-extract para', url)
        const r = await fetch(`/api/cifra-extract?url=${encodeURIComponent(url)}`, {
          signal: AbortSignal.timeout(20000)
        })
        if (r.ok) {
          const d = await r.json()
          const fonteGerado = d?.fonte?.includes('gerado') || d?.fonte?.includes('gemini') || d?.fonte?.includes('groq')
          if (d?.cifra_completa?.length > 60 && temAcordes(d.cifra_completa) && !fonteGerado && !isTemplateCopied(d.cifra_completa)) {
            const resultado = { ...d, titulo: d.titulo || titulo, artista: d.artista || artista, link_cifraclub: url }
            cifraCacheSet(titulo, artista, resultado)
            console.log('[lazy] ✓ cifra real via /api/cifra-extract, fonte:', d.fonte)
            return resultado
          }
          console.log('[lazy] cifra-extract não retornou real, fonte:', d?.fonte, 'sucesso:', d?.sucesso)
        }
      } catch (e) { console.warn('[lazy] cifra-extract erro:', e.message) }
    }

    // 2b. Proxy client-side como fallback (para outros sites que não bloqueiam)
    try {
      console.log('[lazy] tentando proxy client-side para', url)
      const proxyResult = await extrairCifraViaProxy(url)
      if (proxyResult?.cifra_completa?.length > 60 && temAcordes(proxyResult.cifra_completa) && !isTemplateCopied(proxyResult.cifra_completa)) {
        const resultado = { ...proxyResult, titulo: proxyResult.titulo || titulo, artista: proxyResult.artista || artista, link_cifraclub: url }
        cifraCacheSet(titulo, artista, resultado)
        console.log('[lazy] ✓ cifra real via proxy, fonte:', proxyResult.fonte)
        return resultado
      }
      console.log('[lazy] proxy não extraiu cifra válida, fonte:', proxyResult?.fonte)
    } catch (e) { console.warn('[lazy] proxy erro:', e.message) }
  }

  // 3. Gemini (cota separada, mais generosa) — NÃO cacheia geração por IA
  const geminiResult = await gerarCifraComGemini(titulo, artista)
  if (geminiResult?.cifra_completa && !isTemplateCopied(geminiResult.cifra_completa)) {
    return geminiResult  // sem cache: IA pode errar, proxy real deve ter prioridade futura
  }

  // 4. Groq (fallback) — NÃO cacheia geração por IA
  const groqResult = await gerarCifraComGroq(`${titulo} ${artista}`)
  if (groqResult?.cifra_completa && !isTemplateCopied(groqResult.cifra_completa)) {
    return groqResult  // sem cache: não queremos bloquear tentativa futura do proxy
  }
  return null
}

// GERA MÚLTIPLAS CIFRAS EM LOTE — 1 chamada Groq para N artistas
// Evita rate limit (TPM) ao substituir N chamadas por 1 só
// ----------------------------------------------------------------
async function gerarCifrasEmLoteComGroq(pares) {
  // pares = [{titulo, artista, url}, ...]
  if (!GROQ_KEY || !pares?.length) return []
  const lista = pares.map((p, i) => `${i + 1}. "${p.titulo}" - ${p.artista}`).join('\n')
  const prompt = `Gere cifras reais (letra + acordes) para cada música abaixo.
Use os acordes REAIS se conhecer. Se não conhecer → omita do array.

Músicas:
${lista}

Retorne SOMENTE JSON array (sem markdown):
[{"titulo":"...","artista":"...","tom_original":"Lá (Am)","acordes":["Am","G","F","E"],"genero":"Gospel","cifra_completa":"INTRO\\nAm  G  F  E\\n\\nVERSO\\nAm       G\\nPrimeira linha\\nF        E\\nSegunda linha"}]`

  try {
    const r = await chamarGroq(prompt)
    if (!r) return []
    // Tenta extrair JSON array do texto
    const match = r.match(/\[[\s\S]*\]/)
    if (!match) return []
    const arr = JSON.parse(match[0])
    if (!Array.isArray(arr)) return []
    return arr
      .filter(d => d?.cifra_completa?.length > 60 && d?.artista && d?.titulo && temAcordes(d.cifra_completa))
      .map((d, i) => {
        const par = pares[i] || {}
        return {
          titulo: d.titulo || par.titulo,
          artista: d.artista || par.artista,
          tom_original: d.tom_original || null,
          acordes: d.acordes || [],
          genero: d.genero || '',
          cifra_completa: d.cifra_completa,
          fonte: 'groq_gerado',
          link_cifraclub: par.url || `https://www.cifraclub.com.br/${slug(d.artista)}/${slug(d.titulo)}/`
        }
      })
  } catch { return [] }
}

// GERA CIFRA COM GROQ — fallback final quando nenhuma busca funciona
// Usa o conhecimento interno do LLaMA para gerar a cifra real
// ----------------------------------------------------------------
async function gerarCifraComGroq(query) {
  if (!GROQ_KEY) return null
  const prompt = `Você é especialista em cifras musicais brasileiras.
A busca pode conter erros de digitação — corrija antes de responder.
Exemplos: "sigle core" → artista Singlecore, "fernandno" → Fernandinho.

Gere a cifra completa (letra + acordes) para: "${query}"

Regras de formatação:
- Acordes em LINHA SEPARADA acima da letra, alinhados por espaços
- Inclua INTRO, VERSO, REFRÃO, PONTE quando existirem
- Use os acordes REAIS da música se você conhecer
- Se não reconhecer a música após corrigir typos → retorne cifra_completa: null

Retorne SOMENTE JSON (sem markdown):
{"titulo":"nome exato","artista":"artista exato","tom_original":"Sol (G)","acordes":["G","D","Em","C"],"genero":"Gospel","cifra_completa":"INTRO\\n[acordes reais]\\n\\nVERSO\\n[acorde]    [acorde]\\n[letra real da musica aqui]\\n[acorde]    [acorde]\\n[segunda linha real da letra]"}`

  try {
    const r = await chamarGroq(prompt)
    const data = parseJSON(r)
    if (data?.cifra_completa?.length > 60 && data?.artista && data?.titulo && temAcordes(data.cifra_completa)) {
      return {
        titulo: data.titulo, artista: data.artista,
        tom_original: data.tom_original || null,
        acordes: data.acordes || [],
        genero: data.genero || '',
        cifra_completa: data.cifra_completa,
        fonte: 'groq_gerado',
        link_cifraclub: `https://www.cifraclub.com.br/${slug(data.artista)}/${slug(data.titulo)}/`
      }
    }
    return null
  } catch { return null }
}

// ----------------------------------------------------------------
// EXTRAI CIFRA DE QUALQUER SITE VIA PROXY — 5 estratégias em cascata
// Suporta: CifraClub, Cifras.com.br, sites com [G], (G), genérico + IA
// ----------------------------------------------------------------
function limparHTML(raw) {
  return raw
    .replace(/&amp;/g,'&').replace(/&#39;/g,"'")
    .replace(/&lt;/g,'<').replace(/&gt;/g,'>')
    .replace(/&nbsp;/g,' ').replace(/&quot;/g,'"').replace(/&#34;/g,'"')
    .replace(/\r\n/g,'\n').replace(/\r/g,'\n')
    .replace(/\n{3,}/g,'\n\n').trim()
}

function temAcordes(s) {
  return s.length > 60 && /\b[A-G][#b]?m?(7|maj7|dim|aug|sus[24]|add9)?\b/.test(s)
}

export async function extrairCifraViaProxy(url) {
  try {
    const html = await fetchViaProxy(url)
    const lowerUrl = url.toLowerCase()

    // ── Metadados universais ──────────────────────────────────
    const BLACKLIST_TITULO = /^(cifra club|cifraclub|cifras\.com|letras\.mus|vagalume|ultimate guitar|não encontrado|page not found|erro 404|not found|busca|search results)/i

    const tituloM =
      html.match(/<h1[^>]*class="[^"]*(?:title|t1|song-name|cifra-title)[^"]*"[^>]*>([^<]+)<\/h1>/i) ||
      html.match(/<meta[^>]+property="og:title"[^>]+content="([^"]+)"/i)                             ||
      html.match(/<h1[^>]*>([^<]{3,80})<\/h1>/i)
    let titulo = tituloM?.[1]?.replace(/&amp;/g,'&').replace(/\s*[-–|].*$/,'').trim() || null
    if (titulo && BLACKLIST_TITULO.test(titulo)) titulo = null

    // Strings que NÃO são nomes de artistas (promoções, navegação, etc.)
    const BLACKLIST_ARTISTA = /plano anual|assine|premium|cifraclub|cifra club|cadastre|fazer login|entrar grátis|download|aplicativo|\bapp\b|newsletter|publicidade|publicar|anunciar|esqueceu/i

    const artistaM =
      html.match(/class="[^"]*(?:m-artist__name|band-name|artist-name|artista-nome)[^"]*"[^>]*>([^<]+)/i) ||
      html.match(/<a[^>]+itemprop="[^"]*(?:author|performer)[^"]*"[^>]*>([^<]+)<\/a>/i)                  ||
      html.match(/<span[^>]+itemprop="[^"]*(?:author|performer)[^"]*"[^>]*>([^<]+)<\/span>/i)             ||
      html.match(/<h2[^>]*class="[^"]*(?:band|artist|artista)[^"]*"[^>]*>([^<]+)<\/h2>/i)
    let artista = artistaM?.[1]?.replace(/&amp;/g,'&').replace(/&[a-z]+;/gi,'').replace(/\s*[-–|].*$/,'').trim() || null
    if (artista && BLACKLIST_ARTISTA.test(artista)) artista = null

    // Fallback: extrair artista do slug da URL (confiável para CifraClub, BananaCifras, Letras etc.)
    if (!artista) {
      try {
        const parsedUrl = new URL(url)
        const pathParts = parsedUrl.pathname.replace(/\/$/, '').split('/').filter(Boolean)
        // CifraClub: /artista/musica → pathParts[0]
        // BananaCifras: /cifra/c/artista/musica → pathParts[2]  OR /cifra/artista/musica → pathParts[1]
        let artistaSlug = null
        if (parsedUrl.hostname.includes('bananacifras') && pathParts.length >= 3) {
          artistaSlug = pathParts[pathParts.length - 2]
        } else if (pathParts.length >= 1) {
          // Pega primeiro segmento que não seja palavra reservada de rota
          artistaSlug = pathParts.find(p => !/^(cifra|musica|letra|tab|chord|artista|artist|song)$/.test(p)) || null
        }
        if (artistaSlug) {
          artista = artistaSlug.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim()
          if (BLACKLIST_ARTISTA.test(artista)) artista = null
        }
      } catch {}
    }

    const tomM =
      html.match(/data-(?:tone|tom|key|tonalidade)="([A-G][#b]?m?)"/i)                                                    ||
      html.match(/class="[^"]*(?:tom_cifra|tom-cifra|cifra-tom|key-chord|song-key)[^"]*"[^>]*>\s*([A-G][#b]?m?)\s*</i)   ||
      html.match(/<b[^>]*class="[^"]*(?:tom|key|tonalidade)[^"]*"[^>]*>([A-G][#b]?m?)<\/b>/i)                             ||
      html.match(/(?:Tom|Tonalidade|Key|Tom\s+original):\s*<[^>]*>([A-G][#b]?m?)<\//i)                                    ||
      html.match(/(?:Tom|Tonalidade|Key|Tom\s+original):\s*([A-G][#b]?m?)\b/i)                                            ||
      html.match(/<meta[^>]+(?:name|property)="[^"]*(?:music:key|song:key|tone|tom)[^"]*"[^>]+content="([A-G][#b]?m?)"/i)
    let tom_original = tomM?.[1] || null

    // Fallback: detectar tom pela primeira linha de acordes do INTRO/início da cifra
    if (!tom_original) {
      const previewM = html.match(/<pre[^>]*>([\s\S]{1,2000}?)<\/pre>/i)
      if (previewM) {
        const linhas = previewM[1].replace(/<[^>]+>/g, '').split('\n')
        for (const linha of linhas.slice(0, 20)) {
          const acordes = linha.trim().match(/\b([A-G][#b]?m?(7|maj7|dim|aug|sus[24]?|add9)?)\b/g)
          if (acordes && acordes.length >= 1) { tom_original = acordes[0]; break }
        }
      }
    }

    // ── Estratégia 1: CifraClub — múltiplas estratégias DOM + Nuxt ──
    if (lowerUrl.includes('cifraclub.com.br')) {
      // 1a. <pre> com <b> nos acordes (formato clássico)
      const preM = html.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
      if (preM) {
        const cifra = limparHTML(preM[1]
          .replace(/<b>([^<]*)<\/b>/gi, '$1')
          .replace(/<[^>]+>/g, ''))
        if (temAcordes(cifra)) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'cifraclub_proxy' }
      }

      // 1b. Nuxt 3 SSR — cifra no HTML renderizado (classes específicas CifraClub)
      // CifraClub usa classes como: g-cifra, cifra-cnt, song__chord, _cifra-block, tab-content
      const cifraBlockPatterns = [
        /class="[^"]*(?:g-?cifra|cifra[_-]cnt|song__chord|tab[_-]content|_?cifra[_-]block|chord[_-]content)[^"]*"[^>]*>([\s\S]{100,30000}?)<\/(?:div|section|pre|article|code)>/i,
        /id="[^"]*(?:g-?cifra|cifra[_-]cnt|song[_-]content|tab[_-]content|cifra_print)[^"]*"[^>]*>([\s\S]{100,30000}?)<\/(?:div|section|pre|article|code)>/i,
        /<code[^>]*class="[^"]*(?:cifra|chord|tab)[^"]*"[^>]*>([\s\S]{100,30000}?)<\/code>/i,
        /<code[^>]*>([\s\S]{300,30000}?)<\/code>/i,
      ]
      for (const pat of cifraBlockPatterns) {
        const m = html.match(pat)
        if (!m) continue
        // CifraClub Nuxt renderiza acordes em <b> ou em spans
        const raw = m[1]
          .replace(/<b[^>]*>([^<]{1,12})<\/b>/gi, '$1')
          .replace(/<span[^>]*class="[^"]*(?:acorde|chord|note)[^"]*"[^>]*>([^<]+)<\/span>/gi, '$1')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n').replace(/<p[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
        const cifra = limparHTML(raw)
        if (cifra.length > 60 && temAcordes(cifra)) {
          console.log('[proxy] CifraClub: extraiu via bloco DOM')
          return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'cifraclub_dom' }
        }
      }

      // ── Helpers de pontuação (apenas para CifraClub) ──
      // Conta linhas que contêm APENAS acordes (sem letra misturada).
      // É o sinal mais forte de que um bloco é uma cifra real e não uma recomendação.
      const RE_ACORDE_TOK = /^[A-G][#b]?(m|maj|min|dim|aug|sus[24]?|add9?)?(maj7|7|m7|6|9|11|13|b5)?(\/[A-G][#b]?)?$/
      function linhasAcordePuras(texto) {
        let n = 0
        for (const linha of texto.split('\n')) {
          const t = linha.trim()
          if (!t) continue
          const tokens = t.split(/\s+/)
          if (tokens.length >= 1 && tokens.length <= 10 && tokens.every(tok => RE_ACORDE_TOK.test(tok))) n++
        }
        return n
      }
      function scoreCifraLocal(texto) {
        if (!texto || texto.length < 80) return -1
        const temEst = /^\s*(INTRO|VERSO|REFRÃO|PRÉ[- ]?CORO|PONTE|CODA|SOLO|CHORUS|VERSE|BRIDGE)\s*$/im.test(texto) ? 15 : 0
        const lap = linhasAcordePuras(texto)
        return lap * 10 + temEst + Math.min(texto.split('\n').filter(l => l.trim()).length, 80)
      }

      // 1c. Nuxt 3 SSR — varre blocos de texto; seleciona pelo SCORE DE ESTRUTURA
      // (não por densidade bruta — sidebars de recomendação têm alta densidade mas zero linhas puras)
      const htmlSemNoise = html
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<header[\s\S]*?<\/header>/gi, '')
        .replace(/<nav[\s\S]*?<\/nav>/gi, '')
        .replace(/<footer[\s\S]*?<\/footer>/gi, '')
      const chunks = htmlSemNoise.split(/(?=<(?:div|section|article|pre|code)\b)/i)
      let melhorChunk = null, melhorChunkScore = -1
      for (const chunk of chunks) {
        const texto = limparHTML(chunk.replace(/<b[^>]*>([^<]{1,12})<\/b>/gi, '$1').replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, ''))
        if (texto.length < 200 || !temAcordes(texto)) continue
        const s = scoreCifraLocal(texto)
        if (s > melhorChunkScore) { melhorChunkScore = s; melhorChunk = texto }
      }
      // Exige pelo menos 2 linhas de acordes puros — descarta recomendações/sidebars
      if (melhorChunk && linhasAcordePuras(melhorChunk) >= 2) {
        console.log('[proxy] CifraClub: extraiu via score de estrutura (', linhasAcordePuras(melhorChunk), 'linhas acorde)')
        return { titulo, artista, tom_original, cifra_completa: melhorChunk, fonte: 'cifraclub_density' }
      }

      // 1d. __NUXT_DATA__ (formato devalue)
      const nuxtM = html.match(/<script[^>]*id="__NUXT_DATA__"[^>]*>([\s\S]*?)<\/script>/i)
      if (nuxtM) {
        const rawJson = nuxtM[1]
        // Tenta chave "cifra" diretamente no texto JSON
        for (const chave of ['cifra', 'tab', 'chord']) {
          const re = new RegExp('"' + chave + '"\\s*:\\s*"((?:[^"\\\\]|\\\\.){60,})"')
          const km = rawJson.match(re)
          if (km) {
            try {
              const cifraRaw = JSON.parse('"' + km[1] + '"')
              const cifra = limparHTML(cifraRaw.replace(/<b[^>]*>([^<]*)<\/b>/gi, '$1').replace(/<[^>]+>/g, ''))
              if (temAcordes(cifra) && linhasAcordePuras(cifra) >= 1) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'cifraclub_nuxt_proxy' }
            } catch {}
          }
        }
        // Tenta parsear o array devalue
        try {
          const arr = JSON.parse(rawJson)
          // B1: objeto com chave cifra/tab/chord, valor = índice no array
          // Coleta TODOS os candidatos e escolhe o de maior score
          const candidatos = []
          for (const item of arr) {
            if (typeof item !== 'object' || item === null || Array.isArray(item)) continue
            for (const key of ['cifra', 'tab', 'chord']) {
              if (!(key in item)) continue
              const val = item[key]
              let cifraStr = null
              if (typeof val === 'string' && val.length > 60) cifraStr = val
              else if (typeof val === 'number' && val >= 0 && val < arr.length) {
                const deref = arr[val]
                if (typeof deref === 'string' && deref.length > 60) cifraStr = deref
              }
              if (cifraStr) {
                const cifra = limparHTML(cifraStr.replace(/<b[^>]*>([^<]*)<\/b>/gi, '$1').replace(/<[^>]+>/g, ''))
                if (temAcordes(cifra)) candidatos.push(cifra)
              }
            }
          }
          // Escolhe o candidato com maior score de estrutura
          if (candidatos.length > 0) {
            const melhor = candidatos.sort((a, b) => scoreCifraLocal(b) - scoreCifraLocal(a))[0]
            if (melhor && linhasAcordePuras(melhor) >= 1) return { titulo, artista, tom_original, cifra_completa: melhor, fonte: 'cifraclub_nuxt_devalue' }
          }
          // B2: varre TODAS as strings do array devalue pelo score de estrutura
          let melhorCifra = null, melhorScoreB2 = -1
          for (const item of arr) {
            if (typeof item !== 'string' || item.length < 100) continue
            const cifra = limparHTML(item.replace(/<b[^>]*>([^<]*)<\/b>/gi, '$1').replace(/<[^>]+>/g, ''))
            if (!temAcordes(cifra)) continue
            const s = scoreCifraLocal(cifra)
            if (s > melhorScoreB2) { melhorScoreB2 = s; melhorCifra = cifra }
          }
          // Exige pelo menos 2 linhas de acordes puros
          if (melhorCifra && linhasAcordePuras(melhorCifra) >= 2) {
            return { titulo, artista, tom_original, cifra_completa: melhorCifra, fonte: 'cifraclub_nuxt_scored' }
          }
        } catch {}
      }
    }

    // ── Estratégia 1b: BananaCifras — div com <b> para acordes e <br> para linhas ──
    if (lowerUrl.includes('bananacifras.com')) {
      // BananaCifras coloca acordes em <b> dentro de divs, com <br> para quebras
      const divPatterns = [
        /<div[^>]*(?:id|class)="[^"]*(?:tab|cifra|chord|song)[^"]*"[^>]*>([\s\S]+?)<\/div>/i,
        /<article[^>]*>([\s\S]+?)<\/article>/i,
        /<section[^>]*(?:id|class)="[^"]*(?:tab|cifra|chord|content)[^"]*"[^>]*>([\s\S]+?)<\/section>/i,
      ]
      for (const pat of divPatterns) {
        const m = html.match(pat)
        if (!m) continue
        // Converte <b>G</b> → G, <br> → \n, remove demais tags
        const raw = m[1]
          .replace(/<b[^>]*>([^<]{1,10})<\/b>/gi, (_, ac) => {
            // Só substitui se parece um acorde
            return /^[A-G][#b]?[mM]?(7|maj7|dim|aug|sus|add|°|ø)?/.test(ac.trim()) ? ac : `<b>${ac}</b>`
          })
          .replace(/<b[^>]*>([^<]*)<\/b>/gi, '$1')
          .replace(/<br\s*\/?>/gi, '\n')
          .replace(/<\/p>/gi, '\n').replace(/<p[^>]*>/gi, '')
          .replace(/<\/div>/gi, '\n').replace(/<div[^>]*>/gi, '')
          .replace(/<[^>]+>/g, '')
        const cifra = limparHTML(raw)
        if (temAcordes(cifra)) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'bananacifras_proxy' }
      }
      // Fallback: qualquer <pre> na página
      const preM = html.match(/<pre[^>]*>([\s\S]*?)<\/pre>/i)
      if (preM) {
        const cifra = limparHTML(preM[1].replace(/<b>([^<]*)<\/b>/gi,'$1').replace(/<[^>]+>/g,''))
        if (temAcordes(cifra)) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'bananacifras_pre' }
      }
    }

    // ── Estratégia 2: spans class="acorde|chord|tom" (cifras.com.br, outros) ──
    const htmlSemSpans = html.replace(
      /<span[^>]*class="[^"]*(?:acorde|chord|tom|tablature)[^"]*"[^>]*>([^<]+)<\/span>/gi, '$1'
    )
    for (const preM of [...htmlSemSpans.matchAll(/<pre[^>]*>([\s\S]*?)<\/pre>/gi)]) {
      const cifra = limparHTML(preM[1].replace(/<[^>]+>/g, ''))
      if (temAcordes(cifra)) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'span_acorde' }
    }

    // ── Estratégia 3: notação [G] ou (G) inline ────────────────
    const BRACKET_RE = /\[[A-G][^\]]{0,6}\]/
    // Procura em qualquer <pre> ou div de conteúdo
    const candidatos = [
      ...html.matchAll(/<pre[^>]*>([\s\S]*?)<\/pre>/gi),
      ...html.matchAll(/(?:id|class)="[^"]*(?:cifra|chord|tab|lyric|letra|content|body)[^"]*"[^>]*>([\s\S]{100,}?)<\/(?:div|section|article|pre)>/gi),
    ]
    for (const m of candidatos) {
      const raw = m[1]
        .replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
      const limpo = limparHTML(raw)
      if (BRACKET_RE.test(limpo)) {
        const convertido = converterBracketsParaLinhas(limpo)
        if (temAcordes(convertido)) return { titulo, artista, tom_original, cifra_completa: convertido, fonte: 'bracket_notation' }
      }
    }

    // ── Estratégia 4: qualquer <pre> com acordes visíveis ──────
    for (const preM of [...html.matchAll(/<pre[^>]*>([\s\S]*?)<\/pre>/gi)]) {
      const cifra = limparHTML(preM[1]
        .replace(/<b>([^<]*)<\/b>/gi, '$1')
        .replace(/<span[^>]*class="[^"]*(?:acorde|chord)[^"]*"[^>]*>([^<]+)<\/span>/gi, '$1')
        .replace(/<[^>]+>/g, ''))
      if (temAcordes(cifra)) return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'pre_generico' }
    }

    // ── Estratégia 4b: divs com <b> como acordes (BananaCifras, Letras, outros) ──
    // Muitos sites BR usam <b>G</b> dentro de <div> com <br> para quebras de linha
    const htmlSemScript = html.replace(/<script[\s\S]*?<\/script>/gi,'').replace(/<style[\s\S]*?<\/style>/gi,'')
    const divCandidatos = [
      ...htmlSemScript.matchAll(/(?:id|class)="[^"]*(?:cifra|tab|chord|song-content|letra|content|lyric)[^"]*"[^>]*>([\s\S]{80,15000}?)<\/(?:div|section|article)>/gi)
    ]
    for (const m of divCandidatos) {
      const raw = m[1]
        .replace(/<b[^>]*>([^<]{1,12})<\/b>/gi, '$1')
        .replace(/<strong[^>]*>([^<]{1,12})<\/strong>/gi, '$1')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n').replace(/<p[^>]*>/gi, '')
        .replace(/<\/li>/gi, '\n').replace(/<li[^>]*>/gi, '')
        .replace(/<\/div>/gi, '\n').replace(/<div[^>]*>/gi, '')
        .replace(/<[^>]+>/g, '')
      const cifra = limparHTML(raw)
      if (cifra.length > 60 && temAcordes(cifra))
        return { titulo, artista, tom_original, cifra_completa: cifra, fonte: 'div_b_acorde' }
    }

    // ── Estratégia 5: IA extrai do HTML bruto (fallback final) ─
    if (GEMINI_KEY && html.length > 500) {
      try {
        const trecho = html.replace(/<script[\s\S]*?<\/script>/gi,'').replace(/<style[\s\S]*?<\/style>/gi,'').slice(0, 18000)
        const r = parseJSON(await chamarGemini(
          `Este é o HTML de uma página de cifra musical. Extraia a cifra completa com letra e acordes.
Acordes ficam em LINHA SEPARADA acima da letra, alinhados por espaços. Exemplo:
G         D
Primeira linha da letra
Se o HTML NÃO contiver cifra, retorne cifra_completa: null (não escreva explicações).
Retorne APENAS JSON sem markdown:
{"titulo":${JSON.stringify(titulo)},"artista":${JSON.stringify(artista)},"tom_original":null,"cifra_completa":"cifra aqui com \\n para quebras"}
HTML:
${trecho}`
        ))
        if (r?.cifra_completa?.length > 60 && temAcordes(r.cifra_completa))
          return { titulo: r.titulo||titulo, artista: r.artista||artista, tom_original: r.tom_original||tom_original, cifra_completa: r.cifra_completa, fonte: 'ia_html' }
      } catch {}
    }

    // ── Estratégia 5b: Groq (LLaMA) extrai do HTML — fallback quando Gemini falha ─
    if (GROQ_KEY && html.length > 500) {
      try {
        const trecho = html.replace(/<script[\s\S]*?<\/script>/gi,'').replace(/<style[\s\S]*?<\/style>/gi,'').slice(0, 12000)
        const resposta = await chamarGroq(
          `Este é o HTML de uma página de cifra musical brasileira. Extraia a cifra completa.
Acordes ficam em LINHA SEPARADA acima da letra, alinhados por espaços.
IMPORTANTE: se o HTML não contiver cifra (ex: página de busca, erro 404, homepage), retorne cifra_completa com valor null.
NÃO escreva texto explicativo no campo cifra_completa — apenas a cifra ou null.
Retorne SOMENTE JSON válido:
{"titulo":${JSON.stringify(titulo||'')},"artista":${JSON.stringify(artista||'')},"tom_original":"","cifra_completa":"cifra com \\n para quebras de linha"}
HTML: ${trecho}`
        )
        const r = parseJSON(resposta)
        if (r?.cifra_completa?.length > 60 && temAcordes(r.cifra_completa))
          return { titulo: r.titulo||titulo, artista: r.artista||artista, tom_original: r.tom_original||tom_original, cifra_completa: r.cifra_completa, fonte: 'groq_html' }
      } catch {}
    }

    return null
  } catch (e) {
    console.warn('[extrairCifraViaProxy]', e.message)
    return null
  }
}

// ================================================================
// TRANSCRIÇÃO DE ÁUDIO COM GROQ WHISPER verbose_json
// Retorna segmentos com timestamps para alinhar acordes
// ================================================================
async function transcreverAudioVerbose(file) {
  if (!GROQ_KEY) throw new Error('GROQ_KEY não configurada')

  async function chamarWhisper(fd) {
    const r = await fetch('https://api.groq.com/openai/v1/audio/transcriptions', {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${GROQ_KEY}` },
      body: fd,
      signal: AbortSignal.timeout(60000)
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({}))
      throw new Error(err?.error?.message || `Whisper erro ${r.status}`)
    }
    return r.json()
  }

  // Tenta português primeiro, depois detecta automaticamente
  for (const lang of ['pt', null]) {
    try {
      const fd = new FormData()
      fd.append('file', file, file.name)
      fd.append('model', 'whisper-large-v3-turbo')
      fd.append('response_format', 'verbose_json')
      // Pede timestamps POR PALAVRA (além dos de segmento) —
      // necessário para posicionar acordes sobre a palavra exata
      fd.append('timestamp_granularities[]', 'segment')
      fd.append('timestamp_granularities[]', 'word')
      if (lang) fd.append('language', lang)
      const data = await chamarWhisper(fd)
      if (data?.segments?.length > 0) return data
    } catch (e) {
      if (lang === null) throw e
    }
  }
  throw new Error('Whisper não retornou segmentos')
}

// Export simples para quem só precisa do texto
export async function transcreverAudio(file) {
  const data = await transcreverAudioVerbose(file)
  return data?.text || ''
}

// ================================================================
// DETECÇÃO DE ACORDES — Spotify Basic Pitch
// Analisa frequências reais do áudio e retorna acordes com timestamps
// ================================================================
const NOTE_NAMES = ['C','C#','D','Eb','E','F','F#','G','Ab','A','Bb','B']

// Ordenados do mais simples ao mais complexo
// penalty = penalidade aplicada ao score (favorece tríades sobre extensões)
const CHORD_TEMPLATES = [
  { suffix: '',     intervals: [0, 4, 7],       penalty: 0.00 }, // Maior
  { suffix: 'm',    intervals: [0, 3, 7],       penalty: 0.00 }, // Menor
  { suffix: '5',    intervals: [0, 7],           penalty: 0.05 }, // Power chord
  { suffix: 'sus2', intervals: [0, 2, 7],       penalty: 0.08 },
  { suffix: 'sus4', intervals: [0, 5, 7],       penalty: 0.08 },
  { suffix: '7',    intervals: [0, 4, 7, 10],   penalty: 0.12 }, // Dominante 7
  { suffix: 'm7',   intervals: [0, 3, 7, 10],   penalty: 0.12 },
  { suffix: 'maj7', intervals: [0, 4, 7, 11],   penalty: 0.15 },
  { suffix: 'dim',  intervals: [0, 3, 6],       penalty: 0.10 },
  { suffix: 'aug',  intervals: [0, 4, 8],       penalty: 0.10 },
]

// Perfis de Krumhansl-Schmuckler — usados para detectar a tonalidade
const KS_MAJOR = [6.35,2.23,3.48,2.33,4.38,4.09,2.52,5.19,2.39,3.66,2.29,2.88]
const KS_MINOR = [6.33,2.68,3.52,5.38,2.60,3.53,2.54,4.75,3.98,2.69,3.34,3.17]

// Detecta tonalidade dominante a partir do histograma de chroma (12 bins)
function detectarTonalidade(chromaHist) {
  const total = chromaHist.reduce((a,b) => a+b, 0) || 1
  const norm = chromaHist.map(v => v/total)
  let best = { tonic: 0, modo: 'maj', score: -Infinity }
  for (let t = 0; t < 12; t++) {
    let maj = 0, min = 0
    for (let i = 0; i < 12; i++) {
      maj += norm[(t+i) % 12] * KS_MAJOR[i]
      min += norm[(t+i) % 12] * KS_MINOR[i]
    }
    if (maj > best.score) best = { tonic: t, modo: 'maj', score: maj }
    if (min > best.score) best = { tonic: t, modo: 'min', score: min }
  }
  return best
}

// Retorna conjunto de acordes diatônicos (+ dominantes secundárias comuns) da tonalidade
function acordesDaTonalidade({ tonic, modo }) {
  // Graus maiores: I ii iii IV V vi vii°  ou  i ii° III iv v VI VII
  const grausMaj = [
    { i:0, sfx:''   }, // I
    { i:2, sfx:'m'  }, // ii
    { i:4, sfx:'m'  }, // iii
    { i:5, sfx:''   }, // IV
    { i:7, sfx:''   }, // V
    { i:7, sfx:'7'  }, // V7
    { i:9, sfx:'m'  }, // vi
    { i:11,sfx:'dim'}, // vii°
    { i:2, sfx:'7'  }, // II7 (dominante secundária de V)
    { i:9, sfx:'7'  }, // VI7 (dominante secundária de ii)
  ]
  const grausMin = [
    { i:0, sfx:'m'  }, // i
    { i:2, sfx:'dim'}, // ii°
    { i:3, sfx:''   }, // III
    { i:5, sfx:'m'  }, // iv
    { i:7, sfx:'m'  }, // v
    { i:7, sfx:''   }, // V (maior - harmônica)
    { i:7, sfx:'7'  }, // V7
    { i:8, sfx:''   }, // VI
    { i:10,sfx:''   }, // VII
  ]
  const graus = modo === 'maj' ? grausMaj : grausMin
  return graus.map(g => ({
    nome: NOTE_NAMES[(tonic + g.i) % 12] + g.sfx,
    root: (tonic + g.i) % 12,
    sfx: g.sfx
  }))
}

function pitchClassesToChord(pcs, acordesPermitidos = null) {
  const unique = [...new Set(pcs.map(p => ((p % 12) + 12) % 12))]
  if (unique.length < 2) return null

  // Conta frequência de cada pc (pcs pode ter duplicatas pesadas por amplitude)
  const freq = new Array(12).fill(0)
  for (const p of pcs) freq[((p % 12) + 12) % 12]++

  let best = null, bestScore = 0

  const templatesParaTestar = acordesPermitidos
    ? acordesPermitidos.map(a => {
        const tmpl = CHORD_TEMPLATES.find(t => t.suffix === a.sfx)
        return tmpl ? { root: a.root, tmpl } : null
      }).filter(Boolean)
    : (() => {
        const lista = []
        for (let root = 0; root < 12; root++)
          for (const tmpl of CHORD_TEMPLATES) lista.push({ root, tmpl })
        return lista
      })()

  for (const { root, tmpl } of templatesParaTestar) {
    const chordPCs = tmpl.intervals.map(i => (root + i) % 12)
    const hits = chordPCs.filter(pc => unique.includes(pc)).length
    // Peso extra quando a nota fundamental (root) está presente com força
    const rootForce = freq[root] / Math.max(...freq, 1)
    const score = (hits / Math.max(chordPCs.length, unique.length))
                  - tmpl.penalty
                  + rootForce * 0.1
    if (score > bestScore && hits >= 2) {
      bestScore = score
      best = NOTE_NAMES[root] + tmpl.suffix
    }
  }

  return bestScore >= 0.50 ? best : null
}

// Filtro de mediana temporal — remove acordes isolados (ruído)
function suavizarAcordes(seq) {
  if (seq.length < 3) return seq
  const out = [seq[0]]
  for (let i = 1; i < seq.length - 1; i++) {
    const prev = seq[i-1].acorde, cur = seq[i].acorde, next = seq[i+1].acorde
    // Se o atual difere dos dois vizinhos que são iguais → substitui pelo vizinho
    if (cur !== prev && prev === next) {
      out.push({ tempo: seq[i].tempo, acorde: prev })
    } else {
      out.push(seq[i])
    }
  }
  out.push(seq[seq.length-1])
  // Colapsa duplicatas consecutivas
  const final = []
  for (const a of out) {
    if (!final.length || final[final.length-1].acorde !== a.acorde) final.push(a)
  }
  return final
}

async function detectarAcordesPorAudio(file, onProgresso) {
  try {
    onProgresso?.('🎸 Carregando modelo Spotify Basic Pitch...')

    const { BasicPitch, noteFramesToTime, addPitchBendsToNoteEvents, outputToNotesPoly } =
      await import('@spotify/basic-pitch')

    // Lê o arquivo de áudio
    const arrayBuffer = await file.arrayBuffer()

    // Cria AudioContext com sample rate que o Basic Pitch espera
    const AudioCtx = window.AudioContext || window.webkitAudioContext
    if (!AudioCtx) throw new Error('AudioContext não suportado neste browser')
    const audioCtx = new AudioCtx({ sampleRate: 22050 })

    let audioBuffer
    try {
      const decoded = await audioCtx.decodeAudioData(arrayBuffer)

      if (decoded.numberOfChannels === 1) {
        audioBuffer = decoded
      } else {
        // Basic Pitch exige MONO — faz mixdown manual (média dos canais)
        const nCh = decoded.numberOfChannels
        const nFrames = decoded.length
        console.log(`[BasicPitch] mixdown ${nCh}ch → mono (${nFrames} frames @ ${decoded.sampleRate}Hz)`)
        const monoBuffer = audioCtx.createBuffer(1, nFrames, decoded.sampleRate)
        const dst = monoBuffer.getChannelData(0)
        // Coleta todos os canais de uma vez para evitar getChannelData em loop interno
        const srcs = []
        for (let c = 0; c < nCh; c++) srcs.push(decoded.getChannelData(c))
        for (let i = 0; i < nFrames; i++) {
          let s = 0
          for (let c = 0; c < nCh; c++) s += srcs[c][i]
          dst[i] = s / nCh
        }
        audioBuffer = monoBuffer
      }
    } catch (e) {
      throw new Error(`Formato de áudio não suportado: ${e.message}`)
    }

    // ── Filtro bandpass: isola faixa do violão/guitarra ──────────────
    // Remove: bumbo/sub-baixo (< 120Hz) + pratos/voz aguda (> 2kHz)
    // Mantém: fundamentais do violão (80–880Hz) + 1ºs harmônicos (até 2kHz)
    try {
      onProgresso?.('🎸 Isolando frequências do violão...')
      const offCtx = new OfflineAudioContext(1, audioBuffer.length, audioBuffer.sampleRate)
      const src = offCtx.createBufferSource()
      src.buffer = audioBuffer

      // Passa-alta 120Hz — remove bumbo e sub-baixo
      const hp = offCtx.createBiquadFilter()
      hp.type = 'highpass'
      hp.frequency.value = 120
      hp.Q.value = 0.707

      // Passa-baixa 2000Hz — remove pratos, voz aguda, harmônicos altos
      const lp = offCtx.createBiquadFilter()
      lp.type = 'lowpass'
      lp.frequency.value = 2000
      lp.Q.value = 0.707

      src.connect(hp)
      hp.connect(lp)
      lp.connect(offCtx.destination)
      src.start(0)
      audioBuffer = await offCtx.startRendering()
      console.log('[BasicPitch] filtro bandpass 120Hz–2kHz aplicado')
    } catch (e) {
      console.warn('[BasicPitch] filtro falhou, usando áudio direto:', e.message)
    }
    // ─────────────────────────────────────────────────────────────────

    onProgresso?.(`🎸 Analisando ${Math.round(audioBuffer.duration)}s de áudio...`)

    const frames = [], onsets = [], contours = []

    // Modelo local (copiado para public/basic-pitch/) — sem CORS
    const MODEL_URL = window.location.origin + '/basic-pitch/model.json'
    const model = new BasicPitch(MODEL_URL)

    await model.evaluateModel(
      audioBuffer,
      (f, o, c) => { frames.push(...f); onsets.push(...o); contours.push(...c) },
      (p) => onProgresso?.(`🎸 Detectando notas... ${Math.round(p * 100)}%`)
    )

    if (frames.length === 0) throw new Error('Modelo não retornou frames')

    const notes = noteFramesToTime(
      addPitchBendsToNoteEvents(
        contours,
        outputToNotesPoly(frames, onsets, 0.25, 0.25, 5)
      )
    )

    await audioCtx.close()
    console.log(`[BasicPitch] ${notes.length} notas detectadas em ${Math.round(audioBuffer.duration)}s`)

    // ── ETAPA 1: detecção de tonalidade LOCAL (suporta modulação) ──
    // Divide a música em blocos de 30s e detecta o tom de cada um separadamente
    const duracao = audioBuffer.duration
    const BLOCO_TOM = 30.0  // bloco para detecção de tom
    const blocosTom = []
    for (let t = 0; t < duracao; t += BLOCO_TOM) {
      const notasBloco = notes.filter(n =>
        n.startTimeSeconds >= t && n.startTimeSeconds < t + BLOCO_TOM && n.amplitude >= 0.25
      )
      if (notasBloco.length < 10) { blocosTom.push(null); continue }
      const chroma = new Array(12).fill(0)
      for (const n of notasBloco) {
        // Notas graves (bass) peso x2 — fundamental do acorde normalmente está no grave
        const peso = n.pitchMidi < 55 ? n.amplitude * 2 : n.amplitude
        chroma[n.pitchMidi % 12] += peso
      }
      const tonalidade = detectarTonalidade(chroma)
      blocosTom.push({
        inicio: t,
        fim: t + BLOCO_TOM,
        ...tonalidade,
        nome: NOTE_NAMES[tonalidade.tonic] + (tonalidade.modo === 'min' ? 'm' : ''),
        permitidos: acordesDaTonalidade(tonalidade)
      })
    }
    const tomPrincipal = blocosTom.find(b => b) // primeiro não-nulo
    const tomNome = tomPrincipal?.nome || '?'
    const tonsDetectados = [...new Set(blocosTom.filter(b => b).map(b => b.nome))]
    console.log(`[BasicPitch] tonalidades por bloco:`, blocosTom.map(b => b?.nome || '-').join(' → '))
    onProgresso?.(`🎵 Tom: ${tonsDetectados.join(' → ')}`)

    // ── ETAPA 2: agrupa por janelas usando o tom LOCAL de cada janela ──
    const JANELA  = 2.0
    const AMP_MIN = 0.28  // threshold um pouco menor para não perder acordes
    const acordesBrutos = []

    for (let t = 0; t < duracao; t += JANELA) {
      const notasJanela = notes.filter(n =>
        n.startTimeSeconds >= t &&
        n.startTimeSeconds < t + JANELA &&
        n.amplitude >= AMP_MIN
      )
      if (notasJanela.length < 3) continue

      const pitchClasses = notasJanela.flatMap(n => {
        const pc = n.pitchMidi % 12
        // Bass tem peso extra — ajuda a identificar a fundamental do acorde
        const multBass = n.pitchMidi < 55 ? 2 : 1
        const peso = Math.round(n.amplitude * 4 * multBass)
        return Array(Math.max(1, peso)).fill(pc)
      })

      // Usa o tom LOCAL do bloco em que essa janela se encaixa
      const blocoIdx = Math.floor(t / BLOCO_TOM)
      const blocoAtual = blocosTom[blocoIdx] || tomPrincipal
      const permitidos = blocoAtual?.permitidos || null

      const acorde = pitchClassesToChord(pitchClasses, permitidos)
      if (acorde) acordesBrutos.push({ tempo: t, acorde })
    }

    // ── ETAPA 3: colapso de duplicatas consecutivas (sem filtro de mediana) ──
    // Filtro de mediana anterior estava apagando acordes válidos que aparecem só 1x
    const acordesComTempo = []
    for (const a of acordesBrutos) {
      if (!acordesComTempo.length || acordesComTempo[acordesComTempo.length-1].acorde !== a.acorde) {
        acordesComTempo.push(a)
      }
    }
    acordesComTempo.__tomDetectado = tonsDetectados.join(' → ')

    console.log(`[BasicPitch] ${acordesBrutos.length} brutos → ${acordesComTempo.length} únicos consecutivos`)
    return acordesComTempo

  } catch (e) {
    console.error('[BasicPitch] erro:', e.message)
    // Reporta o erro mas não quebra o fluxo — retorna vazio para prosseguir com a letra
    return []
  }
}

// ================================================================
// IDENTIFICA MÚSICA pela letra transcrita + busca cifra real no CifraClub
// Estratégia: Whisper transcreve letra → pega trechos únicos →
// busca "trecho" site:cifraclub.com.br no Google → extrai cifra real via proxy
// ================================================================
async function identificarMusicaPelaLetra(textoLetra, tituloHint, artistaHint, onProgresso) {
  // Limpa e quebra a letra em linhas úteis (sem [Música], sem ..., com letras)
  const linhas = textoLetra
    .split(/[\n.!?]+/)
    .map(l => l.trim().replace(/^\[.*?\]\s*/, ''))
    .filter(l => l.length >= 15 && l.length <= 100 && /[a-záéíóúâêôãõç]/i.test(l))

  // Gera queries em ordem de probabilidade de acerto
  const queries = []

  // 1. Título + artista (se tiver)
  if (tituloHint && artistaHint) queries.push(`${tituloHint} ${artistaHint}`)

  // 2. Trechos únicos da letra em aspas (força match exato no Google)
  //    Pega linhas do meio da música (mais propensas a serem refrão/verso único)
  const linhasUnicas = [...new Set(linhas)]
  const trechos = linhasUnicas.slice(0, 3).concat(linhasUnicas.slice(2, 5))
  for (const trecho of trechos) {
    // Remove pontuação do início/fim, limita a 60 chars
    const limpo = trecho.replace(/^[,;:\-\s]+|[,;:\-\s]+$/g, '').slice(0, 60)
    if (limpo.length >= 15) queries.push(`"${limpo}"`)
  }

  // 3. Só o título (último recurso)
  if (tituloHint) queries.push(tituloHint)

  // Testa cada query, para assim que achar uma cifra real válida
  const queriesTestadas = new Set()
  for (const query of queries) {
    if (queriesTestadas.has(query)) continue
    queriesTestadas.add(query)

    onProgresso?.(`🔍 Buscando cifra: ${query.slice(0, 50)}...`)
    const urls = await buscarUrlsMultiSite(query)
    if (urls.length === 0) continue

    // Testa as 3 primeiras URLs encontradas
    for (const url of urls.slice(0, 3)) {
      try {
        const cifra = await extrairCifraViaProxy(url)
        if (cifra?.cifra_completa?.length > 100 && cifra?.artista && cifra?.titulo) {
          onProgresso?.(`✅ Encontrada: ${cifra.titulo} - ${cifra.artista}`)
          return { ...cifra, link_cifraclub: url }
        }
      } catch {}
    }
  }

  return null
}

// ================================================================
// LLM conhece a música? — usa memória do modelo (Kimi/LLaMA)
// Muitas músicas brasileiras populares estão no treino do LLM
// Se ele conhece, devolve a cifra completa correta
// ================================================================
async function cifraPelaMemoriaDoLLM(titulo, artista, trechoLetra, onProgresso) {
  if (!titulo && !artista && !trechoLetra) return null
  if (!GROQ_KEY) return null

  onProgresso?.('🧠 Consultando memória da IA sobre essa música...')

  const systemPrompt = `Você é um músico brasileiro com conhecimento enciclopédico de cifras de violão/guitarra: sertanejo, gospel, MPB, forró, samba, rock nacional, pagode, axé.

Quando perguntado sobre uma música específica, você pode:
- CONHEÇO: devolver a cifra completa com acordes alinhados sobre as palavras
- NÃO CONHEÇO: responder apenas com "DESCONHECIDA" (uma palavra)

Regras quando conhece a música:
1. Formato texto puro, fonte monoespaçada (use espaços para alinhar acordes sobre palavras)
2. Acordes em LINHA ACIMA da letra, alinhados por coluna
3. Cada estrofe + refrão com acordes completos
4. Use notação brasileira (C, D, Em, F#m, Bb, etc.)
5. Comece com "TOM: [X]" na primeira linha, depois linha em branco, depois a cifra
6. NÃO invente letra ou acordes. Se não lembra com certeza, responda "DESCONHECIDA"
7. NÃO responda músicas que não existem ou que você não tem certeza

Resposta: APENAS cifra (ou "DESCONHECIDA"). Sem markdown. Sem backticks. Sem explicações.`

  const userPrompt = `Você conhece a cifra desta música brasileira?

Título: ${titulo || '(não informado)'}
Artista: ${artista || '(não informado)'}
Trecho da letra transcrita do áudio:
"${(trechoLetra || '').slice(0, 400)}"

Se você tem certeza dessa música específica (mesmo título, mesmo artista, mesma letra), devolva a cifra completa com acordes sobre as palavras.
Se tiver qualquer dúvida ou não conhecer, responda APENAS: DESCONHECIDA`

  const resposta = await chamarGroqTexto(systemPrompt, userPrompt)
  if (!resposta) return null

  const limpa = resposta.trim()
  if (/^DESCONHECIDA\b/i.test(limpa) || limpa.length < 150) {
    console.log('[LLM memória] modelo não conhece a música')
    return null
  }

  // Extrai TOM: X da primeira linha se existir
  let tom = null
  let cifra = limpa
  const mTom = limpa.match(/^TOM:\s*([A-G][#b]?m?)\s*\n\s*\n?([\s\S]+)/i)
  if (mTom) { tom = mTom[1]; cifra = mTom[2].trim() }

  // Valida qualidade mínima: pelo menos 3 acordes distintos
  const acordesDetectados = new Set((cifra.match(/\b[A-G][#b]?m?(?:7|maj7|sus[24])?\b/g) || []))
  if (acordesDetectados.size < 3) {
    console.log('[LLM memória] resposta muito vaga, descartando')
    return null
  }

  console.log(`[LLM memória] cifra recuperada: tom=${tom}, ${acordesDetectados.size} acordes distintos`)
  return { cifra_completa: cifra, tom_original: tom, fonte: 'llm_memoria' }
}

// ================================================================
// GERAR CIFRA A PARTIR DE ARQUIVO DE ÁUDIO — estratégia híbrida em 4 etapas
// 1. Whisper transcreve a letra real
// 2. Tenta identificar música no CifraClub pela letra (acordes 100% corretos)
// 3. Pergunta ao LLM se conhece a música pela memória (Kimi/LLaMA conhecem muitas)
// 4. Último recurso: Basic Pitch detecta acordes do áudio + LLM reconstrói cifra
// ================================================================
export async function gerarCifraPorAudio(file, tituloHint = '', artistaHint = '', onProgresso) {
  // ETAPA 1 — Whisper transcreve a letra (sempre)
  onProgresso?.('🎙️ Transcrevendo letra com Whisper...')
  const resultTranscricao = await transcreverAudioVerbose(file)
  const segments = resultTranscricao?.segments || []
  const textoLetra = resultTranscricao?.text || ''

  if (segments.length === 0 || !textoLetra) {
    throw new Error('Não foi possível transcrever o áudio. Verifique se o arquivo tem voz/letra.')
  }

  // ETAPA 2 — Tenta identificar a música no CifraClub pela letra
  onProgresso?.('🔍 Identificando música pelo trecho da letra...')
  const cifraReal = await identificarMusicaPelaLetra(textoLetra, tituloHint, artistaHint, onProgresso)

  if (cifraReal) {
    // SUCESSO: achou cifra real no CifraClub — retorna ela diretamente
    return {
      titulo: cifraReal.titulo,
      artista: cifraReal.artista,
      tom_original: cifraReal.tom_original || null,
      acordes: [],
      genero: null,
      cifra_completa: cifraReal.cifra_completa,
      link_cifraclub: cifraReal.link_cifraclub,
      fonte: 'audio→cifraclub',
      letraTranscrita: textoLetra
    }
  }

  // ETAPA 3 — Basic Pitch detecta acordes do áudio
  onProgresso?.('⚠️ Música não encontrada nas bases, detectando acordes do áudio...')
  const acordesComTempo = await detectarAcordesPorAudio(file, onProgresso)
  const temAcordes = acordesComTempo.length > 0

  // ETAPA 3b — Memória do LLM: só ativa se Basic Pitch não encontrou acordes suficientes
  // (evita o LLM alucinar quando já temos dados reais do áudio)
  if (!temAcordes || acordesComTempo.length < 5) {
    onProgresso?.('🧠 Basic Pitch sem resultado, consultando memória da IA...')
    const cifraLLM = await cifraPelaMemoriaDoLLM(tituloHint, artistaHint, textoLetra, onProgresso)
    if (cifraLLM) {
      return {
        titulo: tituloHint || null,
        artista: artistaHint || null,
        tom_original: cifraLLM.tom_original,
        acordes: [],
        genero: null,
        cifra_completa: cifraLLM.cifra_completa,
        fonte: 'llm_memoria',
        letraTranscrita: textoLetra
      }
    }
  }

  onProgresso?.('📝 Montando cifra...')
  // (temAcordes já declarada acima, usada nos blocos abaixo)
  const palavrasGlobais = resultTranscricao?.words || []
  let cifra = ''

  if (!temAcordes) {
    // Sem acordes: só letra
    for (const seg of segments) {
      const texto = (seg.text || '').trim().replace(/^\[.*?\]\s*/, '')
      if (texto) cifra += texto + '\n'
    }
  } else if (palavrasGlobais.length > 0) {
    // ═══════════════════════════════════════════════════════════════
    // ALINHAMENTO GLOBAL POR PALAVRA
    // 1. Calcula duração de cada acorde (até o próximo)
    // 2. Atribui cada acorde à palavra mais próxima no tempo
    // 3. Deduplica: mesma palavra recebe só 1 acorde (o mais longo)
    // 4. Agrupa palavras em linhas usando os segmentos
    // 5. Renderiza linha a linha com colunas alinhadas
    // ═══════════════════════════════════════════════════════════════

    // 1. Duração de cada acorde (até o próximo detectado)
    const acordesComDur = acordesComTempo.map((c, i) => {
      const prox = acordesComTempo[i+1]?.tempo ?? (c.tempo + 4)
      return { ...c, duracao: prox - c.tempo }
    })

    // 2. Atribui acorde a palavra mais próxima (tolerância 2.5s)
    const marcacoesPorPalavra = new Map() // idx palavra → {acorde, duracao}
    for (const ch of acordesComDur) {
      let melhorIdx = -1, menorDist = Infinity
      for (let i = 0; i < palavrasGlobais.length; i++) {
        const d = Math.abs(palavrasGlobais[i].start - ch.tempo)
        if (d < menorDist) { menorDist = d; melhorIdx = i }
      }
      if (melhorIdx === -1 || menorDist > 2.5) continue
      const atual = marcacoesPorPalavra.get(melhorIdx)
      // Se já tem acorde, fica com o de maior duração
      if (!atual || ch.duracao > atual.duracao) {
        marcacoesPorPalavra.set(melhorIdx, { acorde: ch.acorde, duracao: ch.duracao })
      }
    }

    // 3. Linhas = segmentos. Cada linha tem seus índices de palavra
    let idxPalavra = 0
    let ultimoAcordeMostrado = null

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      const texto = (seg.text || '').trim().replace(/^\[.*?\]\s*/, '')
      if (!texto) continue

      const segFim = segments[i+1]?.start ?? seg.end ?? (seg.start + 4)

      // Coleta palavras dessa linha
      const palavrasLinha = []
      while (idxPalavra < palavrasGlobais.length) {
        const w = palavrasGlobais[idxPalavra]
        if (w.start >= segFim) break
        palavrasLinha.push({ ...w, idxGlobal: idxPalavra })
        idxPalavra++
      }

      if (palavrasLinha.length === 0) {
        cifra += texto + '\n'
        continue
      }

      // Monta linha de letra + colunas de cada palavra
      let linhaLetra = ''
      const colunas = []
      for (let k = 0; k < palavrasLinha.length; k++) {
        const w = palavrasLinha[k]
        if (k > 0) linhaLetra += ' '
        colunas.push({
          col: linhaLetra.length,
          idxGlobal: w.idxGlobal
        })
        linhaLetra += (w.word || '').trim()
      }

      // Pega marcações que caíram nessa linha
      const marcacoesLinha = []
      for (const col of colunas) {
        const m = marcacoesPorPalavra.get(col.idxGlobal)
        if (m) marcacoesLinha.push({ col: col.col, ...m })
      }

      // Remove marcação igual ao último acorde já mostrado
      // (evita "E" repetido no começo de cada linha quando não mudou)
      if (marcacoesLinha[0]?.acorde === ultimoAcordeMostrado) marcacoesLinha.shift()

      // Deduplica acordes iguais consecutivos dentro da linha
      const marcsDedup = []
      for (const m of marcacoesLinha) {
        if (!marcsDedup.length || marcsDedup[marcsDedup.length-1].acorde !== m.acorde) {
          marcsDedup.push(m)
        }
      }

      // Se sobraram acordes, limita a 4 por linha (se passar, pega os 4 mais longos)
      let marcsFinais = marcsDedup
      if (marcsFinais.length > 4) {
        const top4 = [...marcsFinais].sort((a, b) => b.duracao - a.duracao).slice(0, 4)
        const setTop = new Set(top4.map(m => m.col))
        marcsFinais = marcsFinais.filter(m => setTop.has(m.col))
      }

      // Constrói linha de acordes
      if (marcsFinais.length > 0) {
        let linhaAcordes = ''
        for (const m of marcsFinais) {
          const alvo = Math.max(m.col, linhaAcordes.length + (linhaAcordes.length > 0 ? 1 : 0))
          while (linhaAcordes.length < alvo) linhaAcordes += ' '
          linhaAcordes += m.acorde
        }
        cifra += linhaAcordes + '\n'
        ultimoAcordeMostrado = marcsFinais[marcsFinais.length-1].acorde
      }
      cifra += linhaLetra + '\n'
    }
  } else {
    // Fallback sem word-timestamps: um acorde por linha (o mais longo)
    let acordeAnterior = null
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i]
      const texto = (seg.text || '').trim().replace(/^\[.*?\]\s*/, '')
      if (!texto) continue
      const segFim = segments[i+1]?.start ?? seg.end ?? (seg.start + 4)
      const noSeg = acordesComTempo.filter(c => c.tempo >= seg.start - 0.5 && c.tempo < segFim)
      if (noSeg.length > 0) {
        const c = noSeg[0]
        if (c.acorde !== acordeAnterior) {
          cifra += c.acorde + '\n'
          acordeAnterior = c.acorde
        }
      }
      cifra += texto + '\n'
    }
  }

  const acordesUnicos = [...new Set(acordesComTempo.map(a => a.acorde))]
  const tomDetectado = acordesComTempo.__tomDetectado || acordesUnicos[0] || null

  // ══════════════════════════════════════════════════════════════════
  // REBUILD via LLM — NÃO polir cifra bruta (LLM fica ancorado nos erros)
  // Estratégia: dar só matérias-primas (letra + tom + progressão como dica)
  // e deixar o LLM CONSTRUIR a cifra do zero usando conhecimento musical
  // ══════════════════════════════════════════════════════════════════
  if (textoLetra && textoLetra.length > 50) {
    try {
      onProgresso?.('🤖 IA musical reconstruindo cifra do zero...')

      // Progressão resumida: só nomes únicos em ordem (sem timestamps que confundem o LLM)
      const progressaoResumo = temAcordes
        ? acordesComTempo
            .filter((c, i, arr) => i === 0 || arr[i-1].acorde !== c.acorde)
            .slice(0, 40)
            .map(c => c.acorde)
            .join(' → ')
        : '(nenhum acorde detectado com confiança)'

      // Lista de acordes distintos mais presentes
      const acordesDistintos = temAcordes
        ? [...new Set(acordesComTempo.map(c => c.acorde))].slice(0, 12).join(', ')
        : ''

      const systemPrompt = `Você é um músico profissional brasileiro, especialista em cifras de violão/guitarra para música popular (sertanejo, gospel, MPB, forró, samba, rock nacional, pagode).

Você vai RECONSTRUIR uma cifra a partir destas matérias-primas:
1. Letra da música (transcrita do áudio — preservar exatamente)
2. Tom detectado (pode estar certo ou ter 1 de erro — confiabilidade média)
3. Progressão observada no áudio (lista de acordes em ordem — MUITO RUIDOSA, use só como dica)

Use SEU CONHECIMENTO MUSICAL para construir a cifra coerente:
✓ Use progressões típicas do gênero reconhecido pela letra
✓ Se a letra é gospel/sertanejo/pagode, aplique o vocabulário harmônico do estilo
✓ Inclua acordes menores quando o contexto emocional pede (versos melancólicos frequentemente usam vi)
✓ Varie entre I, IV, V, vi (acordes principais da tonalidade)
✓ Use dominantes secundárias com moderação (V7/V, V7/vi)
✓ Acordes 1 a 3 por linha típica de letra

FORMATO DE SAÍDA:
- Linha de acordes (espaços para alinhar sobre palavras específicas)
- Linha de letra exatamente como transcrita
- Alterne: acorde-letra-acorde-letra...

REGRAS ABSOLUTAS:
1. NÃO altere nenhuma palavra da letra
2. NÃO produza linhas de acordes sozinhas sem uma linha de letra embaixo
3. Use espaços (nunca tabs) para posicionar acordes
4. Notação brasileira: C, D, Em, F#m, Bb, C#m7, A/C#
5. Tom final na primeira linha: "TOM: X"
6. Responda APENAS com o texto da cifra. Nada de markdown, backticks, explicações.`

      const userPrompt = `TOM detectado no áudio: ${tomDetectado || '(incerto)'}
Acordes observados: ${acordesDistintos}
Progressão bruta detectada: ${progressaoResumo}

LETRA TRANSCRITA (preservar exatamente):
${textoLetra.trim()}

Reconstrua a cifra completa: TOM na primeira linha, depois a letra com acordes alinhados acima das palavras certas, usando seu conhecimento de música popular brasileira.`

      const cifraReconstruida = await chamarGroqTexto(systemPrompt, userPrompt)
      console.log('[LLM rebuild] resposta bruta (primeiros 800 chars):',
        (cifraReconstruida || '').slice(0, 800))

      if (cifraReconstruida && cifraReconstruida.trim().length > 100) {
        let limpa = cifraReconstruida
          .replace(/```[a-z]*\n?/gi, '')
          .replace(/```/g, '')
          // remove preâmbulos mais amplos
          .replace(/^(claro|certo|ótimo|perfeito|aqui (está|vai)|segue|cifra (reconstruída|final|pronta))[^\n]*\n+/i, '')
          .trim()

        // Extrai TOM se veio no formato "TOM: X" (aceita também "Tom:" ou espaços variáveis)
        const mTom = limpa.match(/^TOM:?\s*([A-G][#b]?m?)\s*\n+([\s\S]+)/i)
        if (mTom) {
          acordesComTempo.__tomDetectado = mTom[1]
          limpa = mTom[2].trim()
        }

        // Guarda de segurança: se depois da limpeza ficou muito curto, usa a resposta crua
        if (limpa.length < 80) {
          console.warn('[LLM rebuild] pós-limpeza muito curta, usando resposta bruta')
          limpa = cifraReconstruida.trim()
        }

        cifra = limpa
        console.log(`[LLM rebuild] cifra final: ${cifra.length} chars, ${cifra.split('\n').length} linhas`)
      } else {
        console.warn('[LLM rebuild] resposta insuficiente (<100 chars), mantendo cifra do alinhamento manual')
      }
    } catch (e) {
      console.warn('[LLM rebuild] falhou:', e.message)
    }
  }

  return {
    titulo: tituloHint || null,
    artista: artistaHint || null,
    tom_original: tomDetectado,
    acordes: acordesUnicos.slice(0, 10),
    genero: null,
    cifra_completa: cifra.trim(),
    fonte: temAcordes ? 'audio_basic_pitch' : 'audio_letra_sem_acordes',
    letraTranscrita: textoLetra
  }
}

// ================================================================
// BASE LOCAL — resultados instantâneos para músicas frequentes
// ================================================================
const BASE = [
  { titulo:'Pão e Vinho', artista:'Ministério Amor e Adoração', tom_original:'Sol (G)', acordes:['G','D','Em','C','Am'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/ministerio-amor-e-adoracao/pao-e-vinho/',
    busca:['pão e vinho amor e adoração','pão e vinho te apresentamos','como sinal que tu recolhes','ministério amor e adoração pão'],
    cifra:`INTRO\nG  D  Em  C\n\nPRIMEIRA PARTE\nG              D\nPão e vinho Te apresentamos\nEm             C\nNesse altar como sinal\nG              D\nQue Tu recolhes nossa oferta\nEm             C\nTudo o que somos deixamos aqui\n\nREFRÃO\nG         D\nEis-me aqui, Senhor\nEm        C\nToma o que sou\nG         D\nToma o que tenho\nEm        C\nTudo é Teu\nAm        G/B\nTudo o que temos\nC         D    G\nTe entregamos aqui` },
  { titulo:'Pão e Vinho', artista:'Gabriela Rocha', tom_original:'Lá (A)', acordes:['A','E','F#m','D'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/gabriela-rocha/pao-e-vinho/',
    busca:['pão e vinho gabriela rocha'],
    cifra:`VERSO\nA              E\nPão e vinho Te apresentamos\nF#m            D\nNesse altar como sinal\nA              E\nQue Tu recolhes nossa oferta\nF#m            D\nTudo o que somos deixamos aqui\n\nREFRÃO\nA         E\nEis-me aqui, Senhor\nF#m       D\nToma o que sou` },
  { titulo:'Noites Traiçoeiras', artista:'Anjos de Resgate', tom_original:'Sol (G)', acordes:['G','Bm','C','D','Am','Em'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/anjos-de-resgate-musicas/noites-traicoeiras/',
    busca:['noites traiçoeiras gospel','e ainda se vier noite traiçoeira','deus está aqui nesse momento'],
    cifra:`INTRO\nG  Bm  C  D\n\nPRIMEIRA PARTE\nG              Bm\nDeus está aqui nesse momento\nC              D    G\nSua presença é real em meu viver\n\nREFRÃO\nF              D/F#\nE ainda se vier noite traiçoeira\nG\nSe a cruz pesada for\nEm\nCristo estará contigo\nAm      D    G\nMas Deus te quer sorrindo` },
  { titulo:'Noites Traiçoeiras', artista:'Padre Fábio de Melo', tom_original:'Ré (D)', acordes:['D','F#m','Bm','G','A','Em'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/pe-fabio-de-melo/noites-traicoeiras/',
    busca:['noites traiçoeiras padre fabio','deus está aqui neste momento padre'],
    cifra:`VERSO\nD         F#m    Bm\nDeus está aqui neste momento\nG         A      D\nSua presença é real em meu viver\n\nREFRÃO\nG              A\nE ainda se vier, noites traiçoeiras\nF#m            Bm\nSe a cruz pesada for, Cristo estará contigo\nEm             A      D\nMas Deus te quer sorrindo` },
  { titulo:'Noites Traiçoeiras', artista:'Gusttavo Lima', tom_original:'Ré (D)', acordes:['D','A','Bm','G'], genero:'Sertanejo', link_cifraclub:'https://www.cifraclub.com.br/gusttavo-lima/noites-traicoeiras/',
    busca:['nas noites traiçoeiras eu penso em você','saudades passageiras','sertanejo noites'],
    cifra:`VERSO\nD              A\nNas noites traiçoeiras\nBm             G\nEu penso em você\nD              A\nSaudades passageiras\nBm             G\nQue teimam em viver\n\nREFRÃO\nD         A\nMe lembro do seu olhar\nBm        G\nMe lembro do seu sorriso` },
  { titulo:'Sacramento da Comunhão', artista:'Músicas Católicas', tom_original:'Sol (G)', acordes:['G','D','Em','C','Am'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/catolicas/sacramento-da-comunhao/',
    busca:['sacramento da comunhão','senhor quando te vejo no sacramento','jesus rosto humano de deus'],
    cifra:`INTRO\nC  G  C  G  C  D\n\nPRIMEIRA PARTE\nG              D\nSenhor, quando te vejo\nEm\nNo sacramento da comunhão\nC         D    G\nSinto o céu se abrir\nE uma luz a me atingir\nAm         G/B\nEsfriando minha cabeça\nC         D\nE esquentando o meu coração\n\nREFRÃO\nG         D    Em\nJesus, fonte de misericórdia\nQue jorra do templo\nAm    G    D\nJesus, o filho da rainha!\nBm    C    Em\nJesus, rosto divino do homem\nG    C    D    G\nJesus, rosto humano de Deus!\n\nSEGUNDA PARTE\nC             D    Em\nChego muitas vezes em tua casa meu Senhor\nBm    Em    D\nTriste, abatido, precisando de amor\nC         G\nMas depois da comunhão\nAm         G\nTua casa é meu coração` },
  { titulo:'Sacramento da Comunhão', artista:'Anjos de Resgate', tom_original:'Dó (C)', acordes:['C','G','Am','F','Dm'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/anjos-de-resgate-musicas/sacramento-da-comunhao/',
    busca:['sacramento da comunhão anjos de resgate'],
    cifra:`VERSO\nC         G    Am\nSenhor quando te vejo\nNo sacramento da Comunhão\nF         G    C\nSinto o céu se abrir e uma luz a me atingir\n\nREFRÃO\nC         G    Am\nJesus, fonte de misericórdia\nQue jorra do templo\nF    C    Dm   G\nJesus, o filho da rainha!\nEm   Am   F\nJesus, rosto divino do homem\nC    F    G    C\nJesus, rosto humano de Deus!` },
  { titulo:'Sacramento da Comunhão', artista:'Nelsinho Corrêa', tom_original:'Ré (D)', acordes:['D','A','Bm','G','Em'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/nelsinho-correa/sacramento-da-comunhao/',
    busca:['sacramento da comunhão nelsinho correa'],
    cifra:`VERSO\nD    A/C#    Bm    Bm/A\nSenhor, quando te vejo no sacramento da comunhão\nG    A    D    D/F#    G\nSinto o céu se abrir e uma luz a me atingir\nEm7    G    A\nEsfriando minha cabeça e esquentando meu coração\n\nREFRÃO\nD    A/C#    Bm    Bm/A\nJesus, fonte de misericórdia que jorra do templo\nEm    D/F#    A\nJesus, o filho da Rainha\nF#m    Bm    G\nJesus, rosto divino do homem\nD    Em    A    D\nJesus, rosto humano de Deus` },
  { titulo:'Dança de Avivamento', artista:'Diego Fernandes', tom_original:'Ré (D)', acordes:['D','C','A','Bm','Em','G'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/diego-fernandes/danca-de-avivamento/',
    busca:['naquele dia em que gritei ele me ouviu','aleluia transformou o meu lamento em dança','diego fernandes avivamento'],
    cifra:`VERSO\nD              C\nNaquele dia em que gritei\nD              C\nEle me ouviu\nD         C    D    E\nE a tempestade então se acalmou\n\nPRÉ-REFRÃO\nA              Bm\nLivrou minha alma da morte\nA              G\nSou um milagre...aqui estou\n\nREFRÃO\nEm        G\nAleluia...\nD              A\nTransformou o meu lamento\nEm        G\nAleluia...\nD              A\nEm dança de avivamento\n\nPONTE\nC    Em    G    D\nEu não vou parar de cantar\nNão vou parar de dançar!` },
  { titulo:'Dança de Avivamento', artista:'David Quinlan', tom_original:'Sol (G)', acordes:['G','D','Em','C'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/david-quinlan/danca-de-avivamento/',
    busca:['há um som que vem do céu avivamento','vem espírito santo vem transformar'],
    cifra:`VERSO\nG              D\nHá um som que vem do céu\nEm             C\nÉ o som do avivamento\nG              D\nÉ a voz do Espírito\nEm             C\nChamando essa nação\n\nREFRÃO\nG         D\nVem Espírito Santo\nEm        C\nVem transformar\nG         D\nVem Espírito Santo\nEm        C\nVem nos usar` },
  { titulo:'Vitória no Deserto', artista:'Fernandinho', tom_original:'Sol (G)', acordes:['G','D','Em','C'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/fernandinho/vitoria-no-deserto/',
    busca:['vitória no deserto fernandinho','eu sei que no deserto deus também está'],
    cifra:`INTRO\nG  D  Em  C\n\nVERSO\nG              D\nEu sei que no deserto\nEm              C\nDeus também está\nG              D\nE tudo que eu vivo\nEm              C\nEle vai usar\n\nREFRÃO\nG              D\nVitória no deserto\nEm             C\nVitória na dor\nG              D\nVitória quando tudo\nEm             C\nParece impossível` },
  { titulo:'Vitória no Deserto', artista:'Aline Barros', tom_original:'Sol (G)', acordes:['G','D','Em','C'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/aline-barros/vitoria-no-deserto/',
    busca:['quando a noite fria cair sobre mim','por isso eu pulo pulo na presença do rei'],
    cifra:`PRIMEIRA PARTE\nG              D\nQuando a noite fria\nEm             C\nCair sobre mim\nG              D\nMe vi cercado por egípcios\n\nREFRÃO\nG                   D\nPor isso eu pulo, pulo, pulo\nEm             C\nNa presença do rei\nG                   D\nPor isso eu danço, danço, danço\nEm             C\nNa presença do rei` },
  { titulo:'Filho de Davi', artista:'Lucas Meraki', tom_original:'Dó (C)', acordes:['C','G','Am','F','Dm'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/lucas-meraki/filho-de-davi/',
    busca:['jesus filho de davi me cura','és o santo de israel','me contaram que o senhor ia passar'],
    cifra:`INTRO\nC  G  Am  F\n\nPRIMEIRA PARTE\nC              G\nMe contaram que o Senhor ia passar\nDm             G\nE que havia uma chance de eu poder enxergar\n\nREFRÃO\nC         G    Am\nJesus, filho de Davi, me cura\nF              C\nÉs o Santo de Israel\nG              Am\nDiante de ti a tempestade se cala\nF              C\nMeu Deus honra a minha fé` },
  { titulo:'Lugar Secreto', artista:'Gabriela Rocha', tom_original:'Lá (A)', acordes:['A','E','F#m','D'], genero:'Gospel', link_cifraclub:'https://www.cifraclub.com.br/gabriela-rocha/lugar-secreto/',
    busca:['no teu lugar secreto eu quero estar','santo és tu senhor'],
    cifra:`INTRO\nA  E  F#m  D\n\nVERSO\nA                E\nNo teu lugar secreto\nF#m              D\nEu quero estar\n\nREFRÃO\nA         E\nSanto, santo\nF#m       D\nSanto és tu Senhor` },
  { titulo:'Evidências', artista:'Chitãozinho & Xororó', tom_original:'Ré (D)', acordes:['D','A','Bm','G'], genero:'Sertanejo', link_cifraclub:'https://www.cifraclub.com.br/chitaozinho-e-xororo/evidencias/',
    busca:['que diferença faz a chuva','eu preciso de evidências do teu amor'],
    cifra:`VERSO\nD                A\nQue diferença faz a chuva\nBm               G\nQue molha meu jardim\n\nREFRÃO\nD         A\nÉ que eu preciso\nBm        G\nDe evidências do teu amor` },
]

function buscarLocal(q) {
  const n = s => (s||'').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g,'').replace(/[^\w\s]/g,'').trim()
  const qn = n(q), palavras = qn.split(/\s+/).filter(p=>p.length>2)
  return BASE.filter(m => {
    const tit = n(m.titulo), art = n(m.artista)
    const bs = (m.busca||[]).map(n)
    const titArt = tit + ' ' + art
    if (tit.includes(qn) || art.includes(qn) || titArt.includes(qn)) return true
    const palavrasNoTitArt = palavras.filter(p => titArt.includes(p))
    if (palavrasNoTitArt.length >= Math.min(palavras.length, 2)) return true
    if (bs.some(b => b.includes(qn) || (palavras.length>=2 && palavras.filter(p=>b.includes(p)).length>=2))) return true
    return false
  })
}

// ================================================================
// BUSCA PRINCIPAL
// 0. Brave Search API server-side (/api/cifra-search) — sem bot-block
// 1. Google/DDG multi-site via proxy — fallback se Brave não configurada
// 2. Proxy busca cada página de música (SSR — tem conteúdo real)
// 3. Fallback: Groq identifica slugs → proxy busca páginas
// 4. Fallback: Groq gera cifra diretamente
// NUNCA inventa: sem URL verificável → sem resultado
// ================================================================

// ----------------------------------------------------------------
// Brave Search via endpoint server-side (chave segura no servidor)
// ----------------------------------------------------------------
async function buscarViaServerSearch(query) {
  const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  if (isLocalDev) { console.log('[server-search] pulado (localhost)'); return [] }
  try {
    const r = await fetch(`/api/cifra-search?q=${encodeURIComponent(query)}`, {
      signal: AbortSignal.timeout(10000),
    })
    if (!r.ok) { console.warn('[server-search] HTTP', r.status); return [] }
    const data = await r.json()
    console.log(`[server-search] source="${data.source}" urls=${data.urls?.length ?? 0} hasBrave=${data._debug?.hasBrave} hasGoogle=${data._debug?.hasGoogle} brave:`, JSON.stringify(data._debug?.brave))
    if (data.urls?.length > 0) return data.urls
    return []
  } catch (e) {
    console.warn('[server-search] erro:', e.message)
    return []
  }
}

async function buscarNaInternet(texto) {
  const isCifraValida = v => v?.cifra_completa?.length > 50 && temAcordes(v.cifra_completa) && v?.artista?.trim()

  async function extrairUmaUrl(url) {
    const isCifraClub = url.includes('cifraclub.com.br')
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    // CifraClub usa Nuxt — tenta via endpoint server-side (API REST não bloqueada pelo Akamai)
    if (isCifraClub && !isLocalDev) {
      try {
        const r = await fetch(`/api/cifra-extract?url=${encodeURIComponent(url)}`, {
          signal: AbortSignal.timeout(12000)
        })
        if (r.ok) {
          const d = await r.json()
          const fonteGerado = d?.fonte?.includes('gerado') || d?.fonte?.includes('gemini') || d?.fonte?.includes('groq')
          if (d?.cifra_completa?.length > 50 && !fonteGerado) return d
        }
      } catch {}
      // CifraClub bloqueia proxies client-side (Akamai 403) — retorna lazy card com metadados da URL
      // O usuário clica e `gerarCifraPorArtista` tenta novamente sob demanda
      const slugParts = url.replace(/\/$/, '').split('/').filter(Boolean)
      if (slugParts.length >= 2) {
        const toLabel = s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        return {
          titulo: toLabel(slugParts[slugParts.length - 1]),
          artista: toLabel(slugParts[slugParts.length - 2]),
          cifra_completa: null,
          _lazy: true,
          link_cifraclub: url,
          fonte: 'cifraclub_slug',
          tom_original: null,
          genero: '',
          acordes: []
        }
      }
      return null
    }
    // Para outros sites: tenta proxy client-side
    return extrairCifraViaProxy(url)
  }

  async function processarUrls(urls, fonte) {
    if (urls.length === 0) return []
    const resultados = await Promise.allSettled(
      urls.slice(0, 8).map(async url => {
        const cifra = await extrairUmaUrl(url)
        if (!cifra) return null
        // Aceita tanto cifras reais quanto cards lazy (serão preenchidos ao clicar)
        if (cifra._lazy) return { ...cifra, link_cifraclub: url, fonte }
        return isCifraValida(cifra) ? { ...cifra, link_cifraclub: url, fonte } : null
      })
    )
    return resultados.filter(p => p.status === 'fulfilled' && p.value).map(p => p.value)
  }

  // Extrai pares {artista, titulo} a partir de slugs de URLs do CifraClub
  function parsearSlugsCifraClub(urls) {
    const pares = []
    const vistos = new Set()
    for (const u of urls) {
      try {
        const p = new URL(u)
        if (!p.hostname.includes('cifraclub.com.br')) continue
        // path: /artista-slug/titulo-slug/ ou /artista-slug/titulo-slug
        const parts = p.pathname.replace(/^\/|\/$/g, '').split('/')
        if (parts.length < 2) continue
        const artistaSlug = parts[0]
        const tituloSlug  = parts[1]
        const key = `${artistaSlug}|${tituloSlug}`
        if (vistos.has(key)) continue
        vistos.add(key)
        // Converte slug para texto legível: "fernandinho" → "Fernandinho"
        const toLabel = s => s.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
        pares.push({ artista: toLabel(artistaSlug), titulo: toLabel(tituloSlug), url: u })
      } catch {}
    }
    return pares
  }

  // 0. Brave Search API server-side (sem CAPTCHA, sem bot-block)
  const urlsBrave = await buscarViaServerSearch(texto)
  const listaBrave = await processarUrls(urlsBrave, 'brave_real')
  if (listaBrave.length > 0) return listaBrave

  // 0.5 Brave achou URLs mas extração real falhou:
  //     retorna cards "lazy" imediatamente (sem IA) — cifra gerada só quando o usuário clicar
  if (urlsBrave.length > 0) {
    const pares = parsearSlugsCifraClub(urlsBrave)
    if (pares.length > 0) {
      console.log('[busca] retornando', pares.length, 'cards lazy a partir dos slugs Brave')
      return pares.slice(0, 8).map(({ artista, titulo, url }) => ({
        titulo, artista,
        cifra_completa: null,
        _lazy: true,
        link_cifraclub: url,
        fonte: 'brave_slug',
        tom_original: null,
        genero: '',
        acordes: []
      }))
    }
  }

  // 1. Google/DuckDuckGo multi-site via proxy (fallback)
  const urlsGoogle = await buscarUrlsMultiSite(texto)
  const listaGoogle = await processarUrls(urlsGoogle, 'multisite_real')
  if (listaGoogle.length > 0) return listaGoogle

  // 2. Groq identifica slugs → proxy busca páginas reais
  const urlsGroq = await buscarSlugsCifraClub(texto)
  const listaGroq = await processarUrls(urlsGroq, 'cifraclub_real')
  if (listaGroq.length > 0) return listaGroq

  // 3. Groq gera a cifra diretamente (LLaMA conhece músicas brasileiras populares)
  const groqResult = await gerarCifraComGroq(texto)
  if (groqResult?.cifra_completa) {
    console.log('[Groq] cifra gerada diretamente para:', texto)
    return [groqResult]
  }

  return []
}

// ================================================================
// EXPORTS PÚBLICOS
// ================================================================

export async function buscarResultados(texto) {
  const local = buscarLocal(texto)
  const internet = await buscarNaInternet(texto)

  // Mescla: internet primeiro (dados reais), local como complemento
  const visto = new Set()
  const lista = []
  for (const r of internet) {
    if (!r.titulo) continue
    // Rejeita resultados com cifra inválida (texto de erro, sem acordes)
    if (r.cifra_completa && !temAcordes(r.cifra_completa)) continue
    const k = `${(r.titulo||'').toLowerCase()}|${(r.artista||'').toLowerCase()}`
    if (!visto.has(k)) { visto.add(k); lista.push(r) }
  }
  for (const r of local) {
    const k = `${r.titulo.toLowerCase()}|${r.artista.toLowerCase()}`
    if (!visto.has(k)) { visto.add(k); lista.push({ ...r, cifra_completa: r.cifra, fonte: 'base_local' }) }
  }
  return lista.slice(0, 10)
}

export async function buscarCifraPorLink(link) {
  // 1. Tenta via proxy (real, gratuito, sem IA)
  const real = await extrairCifraViaProxy(link)
  if (real?.cifra_completa?.length > 30) return real

  // NOTA: Não geramos cifra por IA quando temos um link.
  // Gerar acordes inventados (ex: G D Em C) é pior do que retornar vazio —
  // o usuário já tem o link e pode ver a cifra real no site de origem.
  return null
}

export async function buscarCifrasIA(texto) {
  const r = await buscarResultados(texto)
  return {
    sugestoes: r.map(m => ({
      titulo: m.titulo, artista: m.artista, tom_original: m.tom_original,
      tom_sugerido: m.tom_original, acordes_principais: m.acordes || [],
      genero: m.genero, dificuldade: m.dificuldade,
      cifra_preview: (m.cifra_completa||m.cifra||'').split('\n').slice(0,4).join('\n'),
      link_referencia: m.link_cifraclub
    })),
    dica_musical: ''
  }
}

export async function buscarCifraCompleta(titulo, artista) {
  const local = BASE.find(m =>
    m.titulo.toLowerCase()===titulo.toLowerCase() &&
    (!artista || m.artista.toLowerCase().includes(artista.toLowerCase()))
  )
  if (local) return { ...local, cifra_completa: local.cifra, fonte: 'local' }
  return buscarCifraPorLink(`https://www.cifraclub.com.br/${slug(artista||titulo)}/${slug(titulo)}/`)
}

export async function analisarImagemCifra(b64, mime='image/jpeg') {
  const prompt = `Você é um leitor especialista de cifras musicais brasileiras. Analise esta imagem e extraia a cifra EXATAMENTE como aparece, preservando cada posição de acorde.

REGRAS OBRIGATÓRIAS DE FORMATAÇÃO:
1. Acordes ficam em linha SEPARADA acima da linha de letra correspondente
2. Use espaços para preservar o alinhamento horizontal: o acorde fica ACIMA da sílaba correta
3. Exemplo correto:
   G         D        Em        C
   Primeira linha da letra aqui
   G         D
   Segunda linha da letra
4. NUNCA coloque acorde no meio da linha de letra
5. NUNCA mova um acorde de posição — extraia exatamente como está na imagem
6. Preserve seções: INTRO, VERSO, PRÉ-REFRÃO, REFRÃO, PONTE, FINAL
7. Se a imagem estiver escura ou difícil, faça o melhor possível

APENAS JSON (sem markdown):
{"titulo":null,"artista":null,"tom":null,"acordes_encontrados":[],"cifra_completa":null,"qualidade_leitura":"boa|regular|ruim","observacoes":""}`

  try {
    const r = parseJSON(await chamarGemini(prompt, b64, mime))
    if (r?.cifra_completa?.length > 30) return r

    // Segunda tentativa com prompt mais simples
    const prompt2 = `Leia esta cifra musical e extraia tudo que conseguir ver. Acordes ficam em linhas separadas acima das letras, com espaços preservando o alinhamento. JSON: {"titulo":null,"artista":null,"tom":null,"acordes_encontrados":[],"cifra_completa":null,"qualidade_leitura":"ruim","observacoes":""}`
    return parseJSON(await chamarGemini(prompt2, b64, mime))
      || { titulo:null, artista:null, tom:null, acordes_encontrados:[], cifra_completa:null, qualidade_leitura:'ruim', observacoes:'Preencha manualmente.' }
  } catch { return { titulo:null, artista:null, tom:null, acordes_encontrados:[], cifra_completa:null, qualidade_leitura:'ruim', observacoes:'Preencha manualmente.' } }
}

export async function extrairCifraDePDF(b64) {
  const prompt = `Você é especialista em extrair cifras musicais de PDFs. Leia o PDF e reproduza a cifra FIELMENTE no campo "cifra_completa".

FORMATO OBRIGATÓRIO da cifra_completa:
- Cada bloco tem: linha de ACORDES (acima) + linha de LETRA (abaixo)
- Os acordes ficam posicionados por ESPAÇOS acima das sílabas exatas onde tocam
- Use apenas espaços simples (nunca tab) para alinhar
- Fonte do visualizador é monoespaçada (Courier New), portanto cada caractere ocupa o mesmo espaço

EXEMPLO — se o PDF mostra acorde "G" acima de "Quan" e "D" acima de "can":
G              D
Quando o galo canta aqui

REGRAS:
1. NUNCA coloque acorde na mesma linha que a letra — sempre em linha separada acima
2. NUNCA mova acordes de posição — se o PDF tem "Am" acima de "a" (3ª sílaba), reproduza com espaços
3. Conte caracteres para alinhar: se "quando o " tem 9 caracteres, o próximo acorde começa na posição 9
4. Preserve seções exatamente: INTRO, VERSO, PRÉ-REFRÃO, REFRÃO, PONTE, FINAL, CODA
5. Preserve letra EXATAMENTE — sem cortar, sem resumir, sem traduzir
6. Se o PDF tem cifra em bloco (acorde + letra repetido por estrofe), copie cada bloco
7. Se não conseguir ler alguma parte, use "?" no lugar do acorde ou letra

ATENÇÃO — erro comum a evitar:
ERRADO (acorde junto com letra):  "G Quando o galo D canta aqui"
CERTO (acorde em linha separada): "G              D\nQuando o galo canta aqui"

Retorne JSON válido sem markdown:
{"titulo":null,"artista":null,"tom":null,"acordes_encontrados":[],"cifra_completa":"texto completo aqui com \\n para quebras de linha","qualidade_leitura":"boa","observacoes":""}`

  try {
    return parseJSON(await chamarGemini(prompt, b64, 'application/pdf'))
      || { titulo:null, artista:null, tom:null, acordes_encontrados:[], cifra_completa:null, qualidade_leitura:'ruim', observacoes:'Não foi possível ler.' }
  } catch { return { titulo:null, artista:null, tom:null, acordes_encontrados:[], cifra_completa:null, qualidade_leitura:'ruim', observacoes:'Não foi possível ler.' } }
}

export async function identificarTomRapido(titulo, artista) {
  const local = BASE.find(m => m.titulo.toLowerCase().includes(titulo.toLowerCase()))
  if (local) return { tom_original: local.tom_original, acordes: local.acordes, capo: null }
  const prompt = `Tom original de "${titulo}" por "${artista||'?'}". JSON: {"tom_original":"Sol (G)","acordes":["G","D","Em","C"],"capo":null}`
  try {
    const [rGroq, rGemini] = await Promise.allSettled([
      chamarGroq(prompt),
      chamarGemini(prompt).catch(() => null)
    ])
    for (const res of [rGroq, rGemini]) {
      const t = res.status === 'fulfilled' ? res.value : null
      const parsed = parseJSON(t)
      if (parsed?.tom_original && parsed.tom_original !== 'Não identificado') return parsed
    }
    return { tom_original:'Não identificado', acordes:[] }
  } catch { return { tom_original:'Não identificado', acordes:[] } }
}

export async function chatMusical(historico, pergunta) {
  try {
    const ctx = historico.map(m=>`${m.role==='user'?'Músico':'IA'}: ${m.content}`).join('\n')
    return await chamarGemini(`Assistente musical brasileiro. Máximo 120 palavras.\n${ctx?'Histórico:\n'+ctx+'\n':''}Músico: ${pergunta}\nIA:`)
  } catch { return 'IA temporariamente indisponível.' }
}

export function fileParaBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result.split(',')[1])
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
